import React, { useEffect, useMemo, useState, useRef } from 'react';
import { ScrollArea } from './ui/scroll-area';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import { 
  Package,
  Landmark,
  Grid3X3,
  Fence,
  Flower2,
  Armchair,
  Table as TableIcon,
  Sparkles,
  Layers,
  X,
  Menu,
  Square,
  RectangleVertical,
  Maximize2,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  PenLine
} from 'lucide-react';
import useStore from '../store/useStore';
import { getCategories, getElements, getStones, seedData, BACKEND_BASE_URL } from '../api';
import { formatDimensions, formatDimension } from '../lib/utils';
import { VARIANT, isPamyatniki } from '../lib/variant';

// Категории, которые скрываем у памятников: фундамент/покрытие/бордюр/ограда
// относятся к участку благоустройства; bench/table/accessories — мебель/декор
// для оформления участка, в карточке одиночного памятника не нужны.
const PAMYATNIKI_HIDDEN_CATEGORIES = new Set([
  'foundation', 'flooring', 'border', 'fence',
  'bench', 'table', 'accessories',
]);

const categoryIcons = {
  foundation: Layers,
  flooring: Grid3X3,
  pedestal: RectangleVertical,
  border: Square,
  monument: Landmark,
  fence: Fence,
  flowerbed: Flower2,
  bench: Armchair,
  table: TableIcon,
  accessories: Sparkles,
  engraving: PenLine
};

export default function Sidebar() {
  const [iconLoadFailed, setIconLoadFailed] = useState(new Set());
  const [widthInput, setWidthInput] = useState('');
  const [lengthInput, setLengthInput] = useState('');
  const {
    categories,
    elements,
    setCategories,
    setElements,
    addPlacedElement,
    perimeterWidth,
    perimeterLength,
    setPerimeterSize,
    selectedFloor,
    setSelectedFloor,
    selectedFoundation,
    setSelectedFoundation,
    selectedFence,
    setSelectedFence,
    selectedBorder,
    setSelectedBorder,
    isSharedView,
    setSharedView,
    placedElements,
    customItems,
    pendingMonument,
    setPendingMonument,
    clearPendingMonument,
    pendingElement,
    setPendingElement,
    clearPendingElement,
    setMobileSidebarOpen,
    flowerbedFilling,
    setFlowerbedFilling
  } = useStore();

  // Calculate total element count
  const elementCount = (placedElements?.length || 0) + 
    (selectedFloor ? 1 : 0) + 
    (selectedFoundation ? 1 : 0) + 
    (selectedFence ? 1 : 0) + 
    (selectedBorder ? 1 : 0) + 
    (customItems?.length || 0);

  const [loading, setLoading] = useState(true);
  // Check if opening from shared link (URL has ?project=)
  const isFromSharedLink = typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('project');
  // Sidebar open only if: no shared link AND no elements
  const [isCollapsed, setIsCollapsed] = useState(isFromSharedLink || elementCount > 0);
  const [isMobile, setIsMobile] = useState(false);
  // На памятниках нет «Размера» и фундамента — стартуем с памятника.
  const [activeTab, setActiveTab] = useState(isPamyatniki ? 'monument' : 'foundation');
  const [activeMobileSection, setActiveMobileSection] = useState(isPamyatniki ? 'monument' : 'size');
  const sidebarRef = useRef(null);

  // Площадь участка (для пересчёта цен "за м²" в итоговую сумму за весь участок)
  const plotArea = perimeterWidth * perimeterLength;

  // Синхронизируем локальные строки с store (например, когда меняются кнопки +/-)
  useEffect(() => {
    setWidthInput(String(perimeterWidth));
  }, [perimeterWidth]);
  useEffect(() => {
    setLengthInput(String(perimeterLength));
  }, [perimeterLength]);

  // Подсказки скролла (показываем стрелки только когда есть куда листать)
  const collapsedBottomRef = useRef(null); // нижнее меню (свернутое, горизонтальное)
  const mobileLeftNavRef = useRef(null); // левое меню (моб., вертикальное)
  const [canScrollBottomLeft, setCanScrollBottomLeft] = useState(false);
  const [canScrollBottomRight, setCanScrollBottomRight] = useState(false);
  const [canScrollLeftNavUp, setCanScrollLeftNavUp] = useState(false);
  const [canScrollLeftNavDown, setCanScrollLeftNavDown] = useState(false);

  const updateScrollHints = () => {
    const elBottom = collapsedBottomRef.current;
    if (elBottom) {
      const maxX = elBottom.scrollWidth - elBottom.clientWidth;
      const x = elBottom.scrollLeft;
      setCanScrollBottomLeft(x > 2);
      setCanScrollBottomRight(x < maxX - 2);
    }

    const elLeft = mobileLeftNavRef.current;
    if (elLeft) {
      const maxY = elLeft.scrollHeight - elLeft.clientHeight;
      const y = elLeft.scrollTop;
      setCanScrollLeftNavUp(y > 2);
      setCanScrollLeftNavDown(y < maxY - 2);
    }
  };

  // Порядок категорий в меню (важно для UX).
  // Требование: после «Тумбы» → «Памятник» → «Цветник».
  const orderedCategories = useMemo(() => {
    const order = [
      'foundation',
      'flooring',
      'pedestal',   // Тумбы
      'monument',   // Памятник
      'flowerbed',  // Цветник
      'border',
      'fence',
      'bench',
      'table',
      'accessories',
    ];
    const idx = new Map(order.map((name, i) => [name, i]));
    const filtered = isPamyatniki
      ? (categories || []).filter((c) => !PAMYATNIKI_HIDDEN_CATEGORIES.has(c.name))
      : (categories || []);
    return [...filtered].sort((a, b) => {
      const ai = idx.has(a.name) ? idx.get(a.name) : 999;
      const bi = idx.has(b.name) ? idx.get(b.name) : 999;
      if (ai !== bi) return ai - bi;
      return (a.name_ru || a.name || '').localeCompare(b.name_ru || b.name || '');
    });
  }, [categories]);

  // Фундамент в каталоге хранится как «1×1 м + толщина» (цена за м²). Для стяжки показываем реальный размер участка; для «Земля» — без размеров
  const isEarthFoundation = (el) =>
    el?.model_type === 'foundation' && (el.name === 'earth' || el.name_ru === 'Земля');

  const handleTabChange = (value) => {
    // При смене вкладки:
    // - если был выбран памятник (цвет/вариант) и есть превью, считаем последний вариант утверждённым и добавляем его один раз
    // - если был обычный объект в примерке, тоже добавляем его один раз
    if (value !== 'monument' && pendingMonument) {
      addPlacedElement({ ...pendingMonument, element_id: pendingMonument.id });
      clearPendingMonument();
    }
    if (pendingElement) {
      addPlacedElement({ ...pendingElement, element_id: pendingElement.id });
      clearPendingElement();
    }
    setActiveTab(value);
  };
  
  // Синхронизировать активную вкладку с загруженными категориями
  useEffect(() => {
    if (orderedCategories.length > 0 && activeTab !== 'size' && !orderedCategories.some(c => c.name === activeTab)) {
      setActiveTab(orderedCategories[0].name);
    }
  }, [orderedCategories, activeTab]);

  // Check if mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Обновлять подсказки скролла при изменениях размеров/категорий/режима
  useEffect(() => {
    updateScrollHints();
    // небольшая задержка, чтобы DOM успел пересчитаться после рендера
    const t = window.setTimeout(updateScrollHints, 0);
    return () => window.clearTimeout(t);
  }, [isMobile, isCollapsed, orderedCategories.length]);

  // Sync sidebar open state to store so DesignerPage can shrink the 3D scene
  useEffect(() => {
    setMobileSidebarOpen(isMobile && !isCollapsed);
  }, [isCollapsed, isMobile]);

  // Click outside to close (mobile only); при закрытии сайдбара:
  // - последний выбранный памятник (с цветом/вариантом) считаем утверждённым и добавляем один раз
  // - последний обычный объект (pendingElement) тоже считаем утверждённым и добавляем один раз
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (window.innerWidth >= 768) return;
      if (!isCollapsed && sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        const state = useStore.getState();
        if (state.pendingMonument) {
          state.addPlacedElement({ ...state.pendingMonument, element_id: state.pendingMonument.id });
          state.clearPendingMonument();
        }
        if (state.pendingElement) {
          state.addPlacedElement({ ...state.pendingElement, element_id: state.pendingElement.id });
          state.clearPendingElement();
        }
        setIsCollapsed(true);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isCollapsed]);

  // Collapse if shared view changes
  useEffect(() => {
    if (isSharedView) {
      setIsCollapsed(true);
    }
  }, [isSharedView]);

  // Reset shared view when user opens menu
  const handleOpenMenu = () => {
    setIsCollapsed(false);
    if (isSharedView) {
      setSharedView(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const [stones, setStones] = useState([]);

  const loadData = async () => {
    try {
      await seedData();
    } catch (e) {}

    try {
      const [catRes, elemRes, stonesRes] = await Promise.all([
        getCategories(VARIANT),
        getElements(undefined, false, VARIANT),
        getStones(true)
      ]);
      setCategories(catRes.data);
      setElements(elemRes.data);
      setStones(stonesRes.data || []);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryElements = (categoryId) => {
    return elements.filter(el => el.category_id === categoryId);
  };

  // Для памятников: варианты из справочника камней (админка) или запасной список.
  const MONUMENT_COLOR_VARIANTS = [
    { color: '#8b0000', name_ru: 'Красный гранит', price_multiplier: 1.0, texture_url: '/textures/красный-гранит.jpg' },
    { color: '#1a1a1a', name_ru: 'Чёрный гранит', price_multiplier: 220 / 130, texture_url: '/textures/черный_гранит.jpeg' },
    { color: '#6b5b4f', name_ru: 'Гранит «Курты»', price_multiplier: 1.15, texture_url: '/textures/курты.webp' },
    { color: '#2d4a24', name_ru: 'Зелёный гранит', price_multiplier: 1.15, texture_url: '/textures/зеленый_гранит.jpg' },
  ];

  // Варианты для памятника: из API (камни с ценой за м³ из админки) или локальный список.
  // Картинки Курты и Чёрного поменяны местами по индексу (1↔2), т.к. порядок камней из API: Красный, Курты, Чёрный.
  const fromStones = stones.length > 0
    ? stones.map((s, i) => {
        const fallbackIdx = i === 1 ? 2 : i === 2 ? 1 : i;
        const fallback = MONUMENT_COLOR_VARIANTS[fallbackIdx];
        return {
          id: s.id,
          name_ru: s.name_ru,
          price_per_cubic_meter: s.price_per_cubic_meter,
          thumbnail_url: s.thumbnail_url || s.texture_url || fallback?.texture_url,
          texture_url: s.texture_url || fallback?.texture_url,
          color: fallback?.color || '#666666',
        };
      })
    : [];
  const greenVariant = MONUMENT_COLOR_VARIANTS[3];
  // Серый гранит заведён камнем в справочнике (админка, id=stone-grey) — приходит из
  // API как 5-й вариант. Своя texture_url/thumbnail_url = /textures/grey_granit.jpg
  // (фолбэка по индексу 4 в MONUMENT_COLOR_VARIANTS нет, поэтому камень несёт их сам).
  const monumentVariants = fromStones.length > 0
    ? (fromStones.length >= 4
        ? fromStones
        : [...fromStones, {
            id: 'stone-green',
            name_ru: greenVariant?.name_ru || 'Зелёный гранит',
            // Фолбэк срабатывает только если API вернул <4 камней (в проде не бывает).
            // Цена в синхроне с админкой (актуальна на 2026-06).
            price_per_cubic_meter: 3_080_000,
            thumbnail_url: greenVariant?.texture_url,
            texture_url: greenVariant?.texture_url,
            color: greenVariant?.color || '#2d4a24',
          }])
    : MONUMENT_COLOR_VARIANTS.map((v, i) => ({ ...v, thumbnail_url: v.texture_url }));

  // Только для цветников: камни + вариант «Бетон» (в памятниках не показываем)
  const FLOWERBED_CONCRETE = {
    id: 'stone-concrete',
    name_ru: 'Бетон',
    price_per_cubic_meter: 200_000,
    color: '#9ca3af', // запасной цвет
    texture_url: '/textures/бетон.jpg',
    thumbnail_url: '/textures/бетон.jpg'
  };
  const flowerbedVariants = [...monumentVariants, FLOWERBED_CONCRETE];

  const [selectedMonumentVariantIndex, setSelectedMonumentVariantIndex] = useState(0);
  const [selectedPedestalVariantIndex, setSelectedPedestalVariantIndex] = useState(0);
  const [selectedFlowerbedVariantIndex, setSelectedFlowerbedVariantIndex] = useState(0);
  // Тип цветника в UI: 'p' = полый (П-образный), 'rectangle' = цельный
  const [selectedFlowerbedType, setSelectedFlowerbedType] = useState('p');
  // FIO popup state for engraving_text pending element
  const [fioLastName, setFioLastName] = useState('');
  const [fioFirstName, setFioFirstName] = useState('');
  const [fioPatronymic, setFioPatronymic] = useState('');

  useEffect(() => {
    const n = monumentVariants.length;
    if (n > 0 && selectedMonumentVariantIndex >= n) {
      setSelectedMonumentVariantIndex(0);
    }
  }, [monumentVariants.length, selectedMonumentVariantIndex]);

  useEffect(() => {
    const n = monumentVariants.length;
    if (n > 0 && selectedPedestalVariantIndex >= n) {
      setSelectedPedestalVariantIndex(0);
    }
  }, [monumentVariants.length, selectedPedestalVariantIndex]);

  useEffect(() => {
    const n = flowerbedVariants.length;
    if (n > 0 && selectedFlowerbedVariantIndex >= n) {
      setSelectedFlowerbedVariantIndex(0);
    }
  }, [flowerbedVariants.length, selectedFlowerbedVariantIndex]);

  // Цена за м³: из камня (админка) или элемент × множитель варианта.
  const getVariantPrice = (element, v) => {
    if (v.price_per_cubic_meter != null) return Math.round(v.price_per_cubic_meter);
    const base = element.price || 0;
    if (v.price_multiplier != null) return Math.round(base * v.price_multiplier);
    return base + (v.price_modifier || 0);
  };

  const handleConfirmMonument = () => {
    if (!pendingMonument) return;
    addPlacedElement({ ...pendingMonument, element_id: pendingMonument.id });
    clearPendingMonument();
  };

  const getColorVariants = (element, categoryName) => {
    if (categoryName !== 'monument') return null;
    const list = element.color_variants && element.color_variants.length > 0
      ? element.color_variants
      : MONUMENT_COLOR_VARIANTS;
    return list;
  };

  // Подтверждение добавления обычного объекта на сцену после "примерки" (для всех, кроме специальных слоёв)
  const handleConfirmElement = () => {
    if (!pendingElement) return;
    const extra = pendingElement.model_type === 'engraving_text'
      ? { lastName: fioLastName, firstName: fioFirstName, patronymic: fioPatronymic }
      : {};
    addPlacedElement({ ...pendingElement, element_id: pendingElement.id, ...extra });
    setFioLastName(''); setFioFirstName(''); setFioPatronymic('');
    clearPendingElement();
  };

  const handleCancelPendingElement = () => {
    setFioLastName(''); setFioFirstName(''); setFioPatronymic('');
    clearPendingElement();
  };

  const handleConfirmFlowerbedElement = (pendingData) => {
    addPlacedElement(pendingData);
    clearPendingElement();
  };

  const handleAddElement = (element) => {
    // Get category system name
    const category = categories.find(c => c.id === element.category_id);
    const categoryName = category?.name || '';
    
    console.log('Adding element:', element.name_ru, 'category:', categoryName, 'model_type:', element.model_type, 'model_url:', element.model_url);
    
    // Check by model_type OR category name
    const isFence = element.model_type === 'fence' || categoryName === 'fence';
    const isBorder = element.model_type === 'border' || categoryName === 'border';
    const isFloor = element.model_type === 'floor' || categoryName === 'flooring';
    const isFoundation = element.model_type === 'foundation' || categoryName === 'foundation';
    
    console.log('isFence:', isFence, 'isBorder:', isBorder);
    
    if (isFloor) {
      setSelectedFloor(element);
    } else if (isFoundation) {
      setSelectedFoundation(element);
    } else if (isFence) {
      console.log('Setting fence with model_url:', element.model_url);
      setSelectedFence(element);
    } else if (isBorder) {
      setSelectedBorder(element);
    } else {
      // Для обычных объектов: первый клик только выбирает карточку и включает "апрув",
      // фактическое добавление на сцену происходит по кнопке "Добавить"
      if (pendingElement && pendingElement.id === element.id) {
        // Повторный клик по той же карточке отменяет примерку
        clearPendingElement();
      } else {
        setPendingElement(element);
      }
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('kk-KZ', {
      style: 'decimal',
      maximumFractionDigits: 0
    }).format(price) + ' ₸';
  };

  if (loading) {
    return (
      <div className="absolute left-2 top-2 z-30">
        <div className="w-12 h-12 bg-white border border-slate-200 rounded-xl shadow-lg flex items-center justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  // Collapsed state - toggle button (desktop) or mini tab bar (mobile)
  if (isCollapsed) {
    if (!isMobile) {
      return (
        <div className="pointer-events-auto absolute left-2 top-2 z-30">
          <Button
            onClick={handleOpenMenu}
            className="h-12 w-12 rounded-xl shadow-lg bg-primary text-white hover:bg-primary/90 border-2 border-white"
            data-testid="sidebar-toggle"
          >
            <Menu className="h-6 w-6" />
          </Button>
        </div>
      );
    }
    // Mobile: mini tab bar at bottom
    return (
      <div className="pointer-events-auto w-full bg-white border-t border-slate-200 shadow-lg">
        <div className="flex justify-center pt-2 pb-1">
          <div className="w-10 h-1 bg-slate-300 rounded-full" />
        </div>
        <div className="relative">
          <div
            ref={collapsedBottomRef}
            onScroll={updateScrollHints}
            className="flex overflow-x-auto px-2 pb-3 scrollbar-none"
          >
            {!isPamyatniki && (
              <button
                onClick={() => { setActiveMobileSection('size'); setIsCollapsed(false); }}
                className="flex flex-col items-center gap-0.5 p-1 text-[10px] text-slate-600 active:text-primary flex-shrink-0 min-w-[50px]"
              >
                <Maximize2 className="h-5 w-5" />
                <span>Размер</span>
              </button>
            )}
            {orderedCategories.map((cat) => {
              const Icon = categoryIcons[cat.name] || Package;
              const iconSrc = cat.icon_url
                ? (cat.icon_url.startsWith('http') ? cat.icon_url : (process.env.REACT_APP_BACKEND_URL || '') + cat.icon_url)
                : null;
              return (
                <button
                  key={cat.name}
                  onClick={() => { setActiveTab(cat.name); setActiveMobileSection(cat.name); setIsCollapsed(false); }}
                  className="flex flex-col items-center gap-0.5 p-1 text-[10px] text-slate-600 active:text-primary flex-shrink-0 min-w-[50px]"
                >
                  {iconSrc ? (
                    <img src={iconSrc} alt="" className="h-5 w-5 object-contain" />
                  ) : (
                    <Icon className="h-5 w-5" />
                  )}
                  <span className="truncate max-w-[40px] text-center">{cat.name_ru.split(' ')[0]}</span>
                </button>
              );
            })}
          </div>
          {/* Подсказки, что меню можно листать влево/вправо */}
          {canScrollBottomLeft && (
            <div className="absolute left-0 top-0 bottom-0 w-10 bg-gradient-to-r from-white to-transparent pointer-events-none flex items-center justify-start pl-1">
              <ChevronLeft className="h-4 w-4 text-slate-400" />
            </div>
          )}
          {canScrollBottomRight && (
            <div className="absolute right-0 top-0 bottom-0 w-10 bg-gradient-to-l from-white to-transparent pointer-events-none flex items-center justify-end pr-1">
              <ChevronRight className="h-4 w-4 text-slate-400" />
            </div>
          )}
        </div>
      </div>
    );
  }

  // Mobile: two-column layout
  if (isMobile) {
    return (
      <div ref={sidebarRef} className="pointer-events-auto w-full flex-1 min-h-0 flex flex-col bg-white border-t border-slate-200 shadow-2xl" data-testid="sidebar">
        {/* Drag handle + close button */}
        <div className="flex items-center justify-between px-3 pt-2 pb-1 flex-shrink-0">
          <div className="flex-1 flex justify-center">
            <div className="w-10 h-1 bg-slate-300 rounded-full" />
          </div>
          <button
            onClick={() => {
              // Явное закрытие сайдбара на мобиле:
              // если есть последний выбранный памятник или обычный объект, считаем его утверждённым и добавляем один раз
              const state = useStore.getState();
              if (state.pendingMonument) {
                state.addPlacedElement({ ...state.pendingMonument, element_id: state.pendingMonument.id });
                state.clearPendingMonument();
              }
              if (state.pendingElement) {
                state.addPlacedElement({ ...state.pendingElement, element_id: state.pendingElement.id });
                state.clearPendingElement();
              }
              setIsCollapsed(true);
            }}
            className="text-slate-400 hover:text-slate-600 p-1"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Two columns */}
        <div className="flex flex-1 min-h-0">
          {/* Left: icon nav */}
          <div className="relative w-16 flex-shrink-0 border-r border-slate-100">
          <div
            ref={mobileLeftNavRef}
            onScroll={updateScrollHints}
            className="h-full overflow-y-auto scrollbar-none"
          >
            {!isPamyatniki && (
              <button
                onClick={() => setActiveMobileSection('size')}
                className={`w-full flex flex-col items-center gap-0.5 py-2 px-1 text-[10px] ${activeMobileSection === 'size' ? 'bg-primary text-white' : 'text-slate-600'}`}
              >
                <Maximize2 className="h-5 w-5" />
                <span>Размер</span>
              </button>
            )}
            {orderedCategories.map((cat) => {
              const Icon = categoryIcons[cat.name] || Package;
              const iconSrc = cat.icon_url
                ? (cat.icon_url.startsWith('http') ? cat.icon_url : (process.env.REACT_APP_BACKEND_URL || '') + cat.icon_url)
                : null;
              const isActive = activeMobileSection === cat.name;
              return (
                <button
                  key={cat.name}
                  onClick={() => { setActiveMobileSection(cat.name); setActiveTab(cat.name); }}
                  className={`w-full flex flex-col items-center gap-0.5 py-2 px-1 text-[10px] ${isActive ? 'bg-primary text-white' : 'text-slate-600'}`}
                >
                  {iconSrc && !iconLoadFailed.has(cat.id) ? (
                    <img src={iconSrc} alt="" className="h-5 w-5 object-contain" onError={() => setIconLoadFailed(prev => new Set(prev).add(cat.id))} />
                  ) : (
                    <Icon className="h-5 w-5" />
                  )}
                  <span className="text-center leading-tight">{cat.name_ru.split(' ')[0]}</span>
                </button>
              );
            })}
          </div>
          {/* Подсказки, что меню можно листать вверх/вниз */}
          {canScrollLeftNavUp && (
            <div className="absolute top-0 left-0 right-0 h-10 bg-gradient-to-b from-white to-transparent pointer-events-none flex items-start justify-center pt-1">
              <ChevronUp className="h-4 w-4 text-slate-400" />
            </div>
          )}
          {canScrollLeftNavDown && (
            <div className="absolute bottom-0 left-0 right-0 h-10 bg-gradient-to-t from-white to-transparent pointer-events-none flex items-end justify-center pb-1">
              <ChevronDown className="h-4 w-4 text-slate-400" />
            </div>
          )}
          </div>

          {/* Right: content */}
          <div className="flex-1 min-w-0 overflow-y-auto p-3">
            {activeMobileSection === 'size' ? (
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-slate-500 mb-1 block">Ширина</label>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setPerimeterSize(Math.max(1, +(perimeterWidth - 0.5).toFixed(2)), perimeterLength)}
                      className="w-8 h-8 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center text-lg font-medium active:bg-slate-200"
                    >−</button>
                    <div className="relative flex-1">
                      <input
                        type="text"
                        inputMode="decimal"
                        value={widthInput}
                        onChange={(e) => setWidthInput(e.target.value)}
                        onBlur={() => {
                          const v = parseFloat(widthInput.replace(',', '.'));
                          if (!isNaN(v) && v >= 1 && v <= 10) setPerimeterSize(+v.toFixed(2), perimeterLength);
                          else setWidthInput(String(perimeterWidth));
                        }}
                        className="w-full h-8 text-center text-[16px] border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30"
                      />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">м</span>
                    </div>
                    <button
                      onClick={() => setPerimeterSize(Math.min(10, +(perimeterWidth + 0.5).toFixed(2)), perimeterLength)}
                      className="w-8 h-8 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center text-lg font-medium active:bg-slate-200"
                    >+</button>
                  </div>
                </div>
                <div>
                  <label className="text-xs text-slate-500 mb-1 block">Длина</label>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setPerimeterSize(perimeterWidth, Math.max(1, +(perimeterLength - 0.5).toFixed(2)))}
                      className="w-8 h-8 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center text-lg font-medium active:bg-slate-200"
                    >−</button>
                    <div className="relative flex-1">
                      <input
                        type="text"
                        inputMode="decimal"
                        value={lengthInput}
                        onChange={(e) => setLengthInput(e.target.value)}
                        onBlur={() => {
                          const v = parseFloat(lengthInput.replace(',', '.'));
                          if (!isNaN(v) && v >= 1 && v <= 10) setPerimeterSize(perimeterWidth, +v.toFixed(2));
                          else setLengthInput(String(perimeterLength));
                        }}
                        className="w-full h-8 text-center text-[16px] border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30"
                      />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">м</span>
                    </div>
                    <button
                      onClick={() => setPerimeterSize(perimeterWidth, Math.min(10, +(perimeterLength + 0.5).toFixed(2)))}
                      className="w-8 h-8 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center text-lg font-medium active:bg-slate-200"
                    >+</button>
                  </div>
                </div>
              </div>
            ) : (
              <Tabs value={activeTab} onValueChange={handleTabChange}>
                {orderedCategories.map((cat) => {
                  const catElements = getCategoryElements(cat.id);
                  const isFlowerbed = cat.name === 'flowerbed';
                  const flowerbedP = isFlowerbed ? catElements.filter((el) => el.flowerbed_shape !== 'rectangle') : [];
                  const flowerbedRect = isFlowerbed ? catElements.filter((el) => el.flowerbed_shape === 'rectangle') : [];
                  const listToRender = isFlowerbed ? [] : catElements;
                  const isFlooring = cat.name === 'flooring';
                  const isMonument = cat.name === 'monument';
                  const isPedestal = cat.name === 'pedestal';

                  return (
                    <TabsContent key={cat.id} value={cat.name} className="mt-0 space-y-1.5">
                      {isFlooring ? (
                        (() => {
                          const plotArea = perimeterWidth * perimeterLength;
                          const thicknessM = 0.02; // стандарт 2 см
                          const graniteElement = catElements.find((e) => e.name === 'granite_tiles');

                          // Все активные напольные элементы кроме granite_tiles (он используется как основа для камней)
                          const regularFloorEls = catElements.filter(e => e.name !== 'granite_tiles' && e.is_active !== false);

                          const activeStones = (stones || []).filter((s) => s.is_active !== false);
                          const orderedStones = [...activeStones].sort((a, b) => {
                            const ao = a.order ?? 0;
                            const bo = b.order ?? 0;
                            if (ao !== bo) return ao - bo;
                            return (a.name_ru || '').localeCompare(b.name_ru || '');
                          });

                          const selectedStoneName = selectedFloor?.colorName;

                          const makeGraniteFloorSelection = (stone) => {
                            const pricePerM3 = Number(stone?.price_per_cubic_meter) || 0;
                            const perM2 = pricePerM3 * thicknessM;
                            setSelectedFloor({
                              ...(graniteElement || {}),
                              name: graniteElement?.name || 'granite_tiles',
                              name_ru: 'Гранитная плитка',
                              price: perM2,
                              price_unit: 'sqmeter',
                              width: 1,
                              depth: 1,
                              height: thicknessM,
                              stone_id: stone?.id,
                              colorName: stone?.name_ru,
                              texture_url: stone?.texture_url || stone?.thumbnail_url || graniteElement?.texture_url,
                              thumbnail_url: stone?.thumbnail_url || graniteElement?.thumbnail_url,
                            });
                          };

                          const floorCard = (element) => {
                            if (!element) return null;
                            const total = Math.round((Number(element.price) || 0) * plotArea);
                            // Иконка пола: сначала берём превью, если его нет — используем картинку текстуры
                            const rawThumb = element.thumbnail_url || element.texture_url;
                            const thumbSrc = rawThumb
                              ? (rawThumb.startsWith('http')
                                ? rawThumb
                                : rawThumb.startsWith('/api/')
                                  ? BACKEND_BASE_URL + rawThumb
                                  : (process.env.PUBLIC_URL || '') + rawThumb)
                              : null;
                            return (
                              <Card
                                key={element.id}
                                className={`transition-colors cursor-pointer hover:border-accent/50 ${selectedFloor?.id === element.id ? 'border-primary bg-primary/5' : ''}`}
                                onClick={() => setSelectedFloor(element)}
                              >
                                <CardContent className="p-2 flex items-center gap-2">
                                  <div className="w-10 h-10 rounded-md flex-shrink-0 overflow-hidden bg-slate-200 flex items-center justify-center">
                                    {thumbSrc ? (
                                      <img src={thumbSrc} alt="" className="w-full h-full object-cover" />
                                    ) : (
                                      <div className="w-full h-full" style={{ backgroundColor: element.color }} />
                                    )}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-xs font-medium text-slate-800 truncate">{element.name_ru}</p>
                                    <p className="text-[10px] text-slate-500">{formatDimensions(perimeterWidth, perimeterLength, element.height)}</p>
                                  </div>
                                  <Badge variant="secondary" className="text-[10px] px-1.5 py-0.5 shrink-0">{formatPrice(total)}</Badge>
                                </CardContent>
                              </Card>
                            );
                          };

                          return (
                            <div className="space-y-2">
                              {/* Гранит: камни из справочника stones */}
                              <div className="space-y-1">
                                <div className="space-y-1.5">
                                  {orderedStones.map((stone) => {
                                    const pricePerM3 = Number(stone.price_per_cubic_meter) || 0;
                                    const volume = plotArea * thicknessM;
                                    const total = Math.round(volume * pricePerM3);
                                    const isSelected = graniteElement && selectedFloor?.name === graniteElement.name && selectedStoneName === stone.name_ru;
                                    const swatchUrl = stone.thumbnail_url || stone.texture_url;
                                    const textureSrc = swatchUrl
                                      ? (swatchUrl.startsWith('http') ? swatchUrl : swatchUrl.startsWith('/api/') ? BACKEND_BASE_URL + swatchUrl : (process.env.PUBLIC_URL || '') + swatchUrl)
                                      : null;
                                    return (
                                      <Card
                                        key={stone.id}
                                        className={`transition-colors cursor-pointer hover:border-accent/50 ${isSelected ? 'border-primary bg-primary/5' : ''}`}
                                        onClick={() => makeGraniteFloorSelection(stone)}
                                      >
                                        <CardContent className="p-2 flex items-center gap-2">
                                          <div className="w-10 h-10 rounded-md flex-shrink-0 overflow-hidden bg-slate-200 flex items-center justify-center">
                                            {textureSrc ? (
                                              <img src={textureSrc} alt="" className="w-full h-full object-cover" />
                                            ) : (
                                              <div className="w-full h-full" />
                                            )}
                                          </div>
                                          <div className="flex-1 min-w-0">
                                            <p className="text-xs font-medium text-slate-800 truncate">{stone.name_ru}</p>
                                            <p className="text-[10px] text-slate-500">{formatDimensions(perimeterWidth, perimeterLength, thicknessM)}</p>
                                          </div>
                                          <Badge variant="secondary" className="text-[10px] px-1.5 py-0.5 shrink-0">{formatPrice(total)}</Badge>
                                        </CardContent>
                                      </Card>
                                    );
                                  })}
                                </div>
                              </div>

                              {/* Все остальные напольные покрытия (плитка, газон, мрамор и др.) */}
                              {regularFloorEls.map(floorCard)}
                            </div>
                          );
                        })()
                      ) : isMonument ? (
                        <>
                          <div className="space-y-1.5">
                            <p className="text-[11px] font-medium text-slate-500 px-0.5 text-center">Выберите цвет</p>
                            <div className="grid grid-cols-4 gap-x-2 gap-y-1 justify-items-center">
                              {monumentVariants.map((v, i) => {
                                const selected = selectedMonumentVariantIndex === i;
                                const swatchUrl = v.thumbnail_url || v.texture_url;
                                const textureSrc = swatchUrl
                                  ? (swatchUrl.startsWith('http') ? swatchUrl : swatchUrl.startsWith('/api/') ? BACKEND_BASE_URL + swatchUrl : (process.env.PUBLIC_URL || '') + swatchUrl)
                                  : null;
                                return (
                                  <div key={i} className="flex flex-col items-center gap-1 w-full min-w-0">
                                    <button type="button"
                                      className={`w-10 h-10 rounded-lg border-2 overflow-hidden transition-all focus:outline-none focus:ring-2 focus:ring-primary shrink-0 ${selected ? 'border-primary ring-2 ring-primary' : 'border-slate-200 hover:border-primary'}`}
                                      style={!textureSrc ? { backgroundColor: v.color || '#666' } : undefined}
                                      onClick={() => setSelectedMonumentVariantIndex(i)} aria-label={v.name_ru}>
                                      {textureSrc ? <img src={textureSrc} alt="" className="w-full h-full object-cover" /> : null}
                                    </button>
                                    <span className="text-[10px] text-slate-600 text-center leading-tight w-full break-words max-w-[4.5rem]">{v.name_ru}</span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                          <div className="space-y-1">
                            {catElements.map((element) => {
                              const variant = monumentVariants[selectedMonumentVariantIndex] || monumentVariants[0];
                              const pricePerM3 = getVariantPrice(element, variant);
                              const volume = (element.width || 1) * (element.height || 1) * (element.depth || 1);
                              // Плоская доплата за форму (поле «Цена установки» элемента) — чтобы цена
                              // на карточке совпадала с итогом (см. useStore.getTotalPrice).
                              const totalPrice = Math.round(volume * pricePerM3) + (isPamyatniki ? (element.installation_price || 0) : 0);
                              const isPendingThis = pendingMonument && pendingMonument.id === element.id;
                              return (
                                <div key={element.id} className="space-y-1.5">
                                  <Card className={`transition-colors cursor-pointer ${isPendingThis ? 'border-primary ring-2 ring-primary/30 bg-primary/5' : 'hover:border-accent/50'}`}
                                    onClick={() => setPendingMonument({ ...element, element_id: element.id, color: variant.color || '#666', texture_url: variant.texture_url, price: pricePerM3, price_unit: 'cubic_meter', colorName: variant.name_ru })}>
                                    <CardContent className="p-2 flex items-center gap-2">
                                      <div className="w-10 h-10 rounded-md flex-shrink-0 bg-slate-200 flex items-center justify-center overflow-hidden">
                                        {element.thumbnail_url ? <img src={element.thumbnail_url.startsWith('http') ? element.thumbnail_url : (process.env.REACT_APP_BACKEND_URL || '') + element.thumbnail_url} alt="" className="w-full h-full object-cover" /> : <Landmark className="w-5 h-5 text-slate-600" />}
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <p className="text-xs font-medium text-slate-800 truncate">{element.name_ru}</p>
                                        <p className="text-[10px] text-slate-500">{formatDimensions(element.width, element.depth, element.height)}</p>
                                      </div>
                                      <Badge variant="secondary" className="text-[10px] px-1.5 py-0.5 shrink-0">{formatPrice(totalPrice)}</Badge>
                                    </CardContent>
                                  </Card>
                                  {isPendingThis && (
                                    <div className="flex gap-1.5 px-1 pt-0.5">
                                      <button type="button" onClick={handleConfirmMonument} className="flex-1 py-1.5 px-3 rounded-lg text-xs font-medium bg-primary/90 text-white hover:bg-primary transition-colors">Добавить</button>
                                      <button type="button" onClick={clearPendingMonument} className="py-1.5 px-3 rounded-lg text-xs text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-colors">Отмена</button>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </>
                      ) : isPedestal ? (
                        <>
                          <div className="space-y-1.5">
                            <p className="text-[11px] font-medium text-slate-500 px-0.5 text-center">Выберите цвет</p>
                            <div className="grid grid-cols-4 gap-x-2 gap-y-1 justify-items-center">
                              {monumentVariants.map((v, i) => {
                                const selected = selectedPedestalVariantIndex === i;
                                const swatchUrl = v.thumbnail_url || v.texture_url;
                                const textureSrc = swatchUrl
                                  ? (swatchUrl.startsWith('http') ? swatchUrl : swatchUrl.startsWith('/api/') ? BACKEND_BASE_URL + swatchUrl : (process.env.PUBLIC_URL || '') + swatchUrl)
                                  : null;
                                return (
                                  <div key={i} className="flex flex-col items-center gap-1 w-full min-w-0">
                                    <button type="button"
                                      className={`w-10 h-10 rounded-lg border-2 overflow-hidden transition-all focus:outline-none focus:ring-2 focus:ring-primary shrink-0 ${selected ? 'border-primary ring-2 ring-primary' : 'border-slate-200 hover:border-primary'}`}
                                      style={!textureSrc ? { backgroundColor: v.color || '#666' } : undefined}
                                      onClick={() => setSelectedPedestalVariantIndex(i)} aria-label={v.name_ru}>
                                      {textureSrc ? <img src={textureSrc} alt="" className="w-full h-full object-cover" /> : null}
                                    </button>
                                    <span className="text-[10px] text-slate-600 text-center leading-tight w-full break-words max-w-[4.5rem]">{v.name_ru}</span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                          <div className="space-y-1">
                            {catElements.map((element) => {
                              const variant = monumentVariants[selectedPedestalVariantIndex] || monumentVariants[0];
                              const pricePerM3 = getVariantPrice(element, variant);
                              const volume = (element.width || 1) * (element.height || 1) * (element.depth || 1);
                              // Плоская доплата за форму (поле «Цена установки» элемента) — чтобы цена
                              // на карточке совпадала с итогом (см. useStore.getTotalPrice).
                              const totalPrice = Math.round(volume * pricePerM3) + (isPamyatniki ? (element.installation_price || 0) : 0);
                              const isPendingThis = pendingElement && pendingElement.id === element.id;
                              return (
                                <div key={element.id} className="space-y-1.5">
                                  <Card className={`transition-colors cursor-pointer ${isPendingThis ? 'border-primary ring-2 ring-primary/30 bg-primary/5' : 'hover:border-accent/50'}`}
                                    onClick={() => setPendingElement({ ...element, element_id: element.id, color: variant.color || '#666', texture_url: variant.texture_url, price: pricePerM3, price_unit: 'cubic_meter', colorName: variant.name_ru })}>
                                    <CardContent className="p-2 flex items-center gap-2">
                                      <div className="w-10 h-10 rounded-md flex-shrink-0 bg-slate-200 flex items-center justify-center overflow-hidden">
                                        {element.thumbnail_url ? <img src={element.thumbnail_url.startsWith('http') ? element.thumbnail_url : (process.env.REACT_APP_BACKEND_URL || '') + element.thumbnail_url} alt="" className="w-full h-full object-cover" /> : <RectangleVertical className="w-5 h-5 text-slate-600" />}
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <p className="text-xs font-medium text-slate-800 truncate">{element.name_ru}</p>
                                        <p className="text-[10px] text-slate-500">{formatDimensions(element.width, element.depth, element.height)}</p>
                                      </div>
                                      <Badge variant="secondary" className="text-[10px] px-1.5 py-0.5 shrink-0">{formatPrice(totalPrice)}</Badge>
                                    </CardContent>
                                  </Card>
                                  {isPendingThis && (
                                    <div className="flex gap-1.5 px-1 pt-0.5">
                                      <button type="button" onClick={handleConfirmElement} className="flex-1 py-1.5 px-3 rounded-lg text-xs font-medium bg-primary/90 text-white hover:bg-primary transition-colors">Добавить</button>
                                      <button type="button" onClick={handleCancelPendingElement} className="py-1.5 px-3 rounded-lg text-xs text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-colors">Отмена</button>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </>
                      ) : isFlowerbed ? (
                        <>
                          <div className="space-y-1.5">
                            <p className="text-[11px] font-medium text-slate-500 px-0.5 text-center">Выберите материал</p>
                            <div className="grid grid-cols-4 gap-x-2 gap-y-1 justify-items-center">
                              {flowerbedVariants.map((v, i) => {
                                const selected = selectedFlowerbedVariantIndex === i;
                                const swatchUrl = v.thumbnail_url || v.texture_url;
                                const textureSrc = swatchUrl
                                  ? (swatchUrl.startsWith('http') ? swatchUrl : swatchUrl.startsWith('/api/') ? BACKEND_BASE_URL + swatchUrl : (process.env.PUBLIC_URL || '') + swatchUrl)
                                  : null;
                                return (
                                  <div key={i} className="flex flex-col items-center gap-1 w-full min-w-0">
                                    <button type="button"
                                      className={`w-10 h-10 rounded-lg border-2 overflow-hidden transition-all focus:outline-none focus:ring-2 focus:ring-primary shrink-0 ${selected ? 'border-primary ring-2 ring-primary' : 'border-slate-200 hover:border-primary'}`}
                                      style={!textureSrc ? { backgroundColor: v.color || '#666' } : undefined}
                                      onClick={() => setSelectedFlowerbedVariantIndex(i)} aria-label={v.name_ru}>
                                      {textureSrc ? <img src={textureSrc} alt="" className="w-full h-full object-cover" /> : null}
                                    </button>
                                    <span className="text-[10px] text-slate-600 text-center leading-tight w-full break-words max-w-[4.5rem]">{v.name_ru}</span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                          {/* Выберите тип цветника */}
                          <div className="space-y-1.5">
                            <p className="text-[11px] font-medium text-slate-500 px-0.5 text-center">Выберите тип</p>
                            <div className="grid grid-cols-2 gap-2">
                              <button
                                type="button"
                                onClick={() => setSelectedFlowerbedType('p')}
                                className={`h-9 rounded-lg border text-xs font-medium transition-colors ${
                                  selectedFlowerbedType === 'p'
                                    ? 'border-primary bg-primary/5 text-primary'
                                    : 'border-slate-200 hover:border-primary text-slate-700'
                                }`}
                              >
                                Полый
                              </button>
                              <button
                                type="button"
                                onClick={() => setSelectedFlowerbedType('rectangle')}
                                className={`h-9 rounded-lg border text-xs font-medium transition-colors ${
                                  selectedFlowerbedType === 'rectangle'
                                    ? 'border-primary bg-primary/5 text-primary'
                                    : 'border-slate-200 hover:border-primary text-slate-700'
                                }`}
                              >
                                Цельный
                              </button>
                            </div>
                          </div>
                          {/* Наполнение цветника */}
                          {selectedFlowerbedType === 'p' && (() => {
                            const flooringCat = categories?.find(c => c.name === 'flooring');
                            const flooringEls = flooringCat ? elements.filter(e => e.category_id === flooringCat.id && e.is_flowerbed_filling === true && e.is_active !== false) : [];
                            const EARTH = { id: '__earth__', name_ru: 'Земля', price: 0, thumbnail_url: '/textures/zemlya.jpg' };
                            const fillingOptions = [EARTH, ...flooringEls];
                            return (
                              <div className="space-y-1.5">
                                <p className="text-[11px] font-medium text-slate-500 px-0.5 text-center">Наполнение</p>
                                <div className="grid grid-cols-3 gap-x-2 gap-y-1 justify-items-center">
                                  {fillingOptions.map((opt) => {
                                    const isSelected = flowerbedFilling?.id === opt.id || (!flowerbedFilling && opt.id === '__earth__');
                                    // Если отдельного превью нет, пробуем использовать ту же картинку, что и для текстуры
                                    const raw = opt.thumbnail_url || opt.texture_url;
                                    const src = raw
                                      ? (raw.startsWith('http') ? raw : raw.startsWith('/api/') ? BACKEND_BASE_URL + raw : (process.env.PUBLIC_URL || '') + raw)
                                      : null;
                                    return (
                                      <div key={opt.id} className="flex flex-col items-center gap-1 w-full min-w-0">
                                        <button
                                          type="button"
                                          onClick={() => setFlowerbedFilling(opt.id === '__earth__' ? null : opt)}
                                          className={`w-10 h-10 rounded-lg border-2 overflow-hidden transition-all focus:outline-none shrink-0 ${isSelected ? 'border-primary ring-2 ring-primary' : 'border-slate-200 hover:border-primary'}`}
                                          style={!src ? { backgroundColor: '#8B6914' } : undefined}
                                        >
                                          {src ? <img src={src} alt="" className="w-full h-full object-cover" /> : null}
                                        </button>
                                        <span className="text-[10px] text-slate-600 text-center leading-tight w-full break-words max-w-[4.5rem]">
                                          {opt.name_ru}
                                          {opt.price > 0 ? <><br /><span className="text-slate-400">{opt.price.toLocaleString('ru-RU')} ₸/м²</span></> : <><br /><span className="text-slate-400">Бесплатно</span></>}
                                        </span>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })()}
                          {selectedFlowerbedType === 'p' && flowerbedP.length > 0 && (
                            <div className="space-y-1">
                              {flowerbedP.map((element) => {
                                const variant = flowerbedVariants[selectedFlowerbedVariantIndex] || flowerbedVariants[0];
                                const pricePerM3 = getVariantPrice(element, variant);
                                const w = element.width || 1, h = element.height || 1, d = element.depth || 1;
                                const borderThick = 0.08;
                                const volume = borderThick * h * (2 * d + w);
                                const innerW = Math.max(0, w - 2 * borderThick);
                                const innerD = Math.max(0, d - borderThick);
                                const effectivePricePerM3 = pricePerM3 * ((element.price_ratio ?? 100) / 100);
                                const fillingPrice = flowerbedFilling ? Math.round((Number(flowerbedFilling.price) || 0) * innerW * innerD) : 0;
                                const totalPrice = Math.round(volume * effectivePricePerM3) + fillingPrice;
                                const isPendingThis = pendingElement && pendingElement.id === element.id;
                                return (
                                  <div key={element.id} className="space-y-1.5">
                                    <Card className={`transition-colors cursor-pointer ${isPendingThis ? 'border-primary ring-2 ring-primary/30 bg-primary/5' : 'hover:border-accent/50'}`}
                                      onClick={() => setPendingElement({ ...element, element_id: element.id, color: variant.color || '#666', texture_url: variant.texture_url, price: pricePerM3 * ((element.price_ratio ?? 100) / 100), price_unit: 'cubic_meter', colorName: variant.name_ru, flowerbed_shape: element.flowerbed_shape ?? 'p' })}>
                                      <CardContent className="p-2 flex items-center gap-2">
                                        <div className="w-7 h-7 rounded-md flex-shrink-0 overflow-hidden bg-slate-200 flex items-center justify-center">
                                          {element.thumbnail_url ? <img src={element.thumbnail_url.startsWith('http') ? element.thumbnail_url : BACKEND_BASE_URL + element.thumbnail_url} alt="" className="w-full h-full object-contain" /> : variant.thumbnail_url || variant.texture_url ? <img src={(variant.thumbnail_url || variant.texture_url).startsWith('http') ? (variant.thumbnail_url || variant.texture_url) : BACKEND_BASE_URL + (variant.thumbnail_url || variant.texture_url)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full" style={{ backgroundColor: variant.color || '#666' }} />}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                          <p className="text-xs font-medium text-slate-800 truncate">{element.name_ru}</p>
                                          <p className="text-[10px] text-slate-500">{formatDimensions(element.width, element.depth, element.height)}</p>
                                        </div>
                                        <Badge variant="secondary" className="text-[10px] px-1.5 py-0.5">{formatPrice(totalPrice)}</Badge>
                                      </CardContent>
                                    </Card>
                                    <div className="flex gap-1.5 px-1 pt-0.5">
                                      <button type="button" onClick={() => handleConfirmFlowerbedElement({ ...element, element_id: element.id, color: variant.color || '#666', texture_url: variant.texture_url, price: pricePerM3 * ((element.price_ratio ?? 100) / 100), price_unit: 'cubic_meter', colorName: variant.name_ru, flowerbed_shape: element.flowerbed_shape ?? 'p' })} className="flex-1 py-1.5 px-3 rounded-lg text-xs font-medium bg-primary/90 text-white hover:bg-primary transition-colors">Добавить</button>
                                      <button type="button" onClick={handleCancelPendingElement} className="py-1.5 px-3 rounded-lg text-xs text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-colors">Отмена</button>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                          {selectedFlowerbedType === 'rectangle' && flowerbedRect.length > 0 && (
                            <div className="space-y-1">
                              {flowerbedRect.map((element) => {
                                const variant = flowerbedVariants[selectedFlowerbedVariantIndex] || flowerbedVariants[0];
                                const pricePerM3 = getVariantPrice(element, variant);
                                const effectivePricePerM3Rect = pricePerM3 * ((element.price_ratio ?? 100) / 100);
                                const w = element.width || 1, d = element.depth || 1;
                                const volume = w * (element.height || 1) * d;
                                const totalPrice = Math.round(volume * effectivePricePerM3Rect);
                                const isPendingThis = pendingElement && pendingElement.id === element.id;
                                return (
                                  <div key={element.id} className="space-y-1.5">
                                    <Card className={`transition-colors cursor-pointer ${isPendingThis ? 'border-primary ring-2 ring-primary/30 bg-primary/5' : 'hover:border-accent/50'}`}
                                      onClick={() => setPendingElement({ ...element, element_id: element.id, color: variant.color || '#666', texture_url: variant.texture_url, price: effectivePricePerM3Rect, price_unit: 'cubic_meter', colorName: variant.name_ru, flowerbed_shape: 'rectangle' })}>
                                      <CardContent className="p-2 flex items-center gap-2">
                                        <div className="w-7 h-7 rounded-md flex-shrink-0 overflow-hidden bg-slate-200 flex items-center justify-center">
                                          {element.thumbnail_url ? <img src={element.thumbnail_url.startsWith('http') ? element.thumbnail_url : BACKEND_BASE_URL + element.thumbnail_url} alt="" className="w-full h-full object-contain" /> : variant.thumbnail_url || variant.texture_url ? <img src={(variant.thumbnail_url || variant.texture_url).startsWith('http') ? (variant.thumbnail_url || variant.texture_url) : BACKEND_BASE_URL + (variant.thumbnail_url || variant.texture_url)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full" style={{ backgroundColor: variant.color || '#666' }} />}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                          <p className="text-xs font-medium text-slate-800 truncate">{element.name_ru}</p>
                                          <p className="text-[10px] text-slate-500">{formatDimensions(element.width, element.depth, element.height)}</p>
                                        </div>
                                        <Badge variant="secondary" className="text-[10px] px-1.5 py-0.5">{formatPrice(totalPrice)}</Badge>
                                      </CardContent>
                                    </Card>
                                    <div className="flex gap-1.5 px-1 pt-0.5">
                                      <button type="button" onClick={() => handleConfirmFlowerbedElement({ ...element, element_id: element.id, color: variant.color || '#666', texture_url: variant.texture_url, price: effectivePricePerM3Rect, price_unit: 'cubic_meter', colorName: variant.name_ru, flowerbed_shape: 'rectangle' })} className="flex-1 py-1.5 px-3 rounded-lg text-xs font-medium bg-primary/90 text-white hover:bg-primary transition-colors">Добавить</button>
                                      <button type="button" onClick={handleCancelPendingElement} className="py-1.5 px-3 rounded-lg text-xs text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-colors">Отмена</button>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </>
                      ) : (
                        listToRender.map((element) => {
                          // Важно: в админке некоторые элементы могут быть в категории "Оградка/Бордюры",
                          // но иметь другой model_type. Поэтому считаем "периметр" и по категории тоже.
                          const isFence = element.model_type === 'fence' || cat.name === 'fence';
                          const isBorder = element.model_type === 'border' || cat.name === 'border';
                          const isPerimeterBased = isFence || isBorder;
                          const perimeterMeters = 2 * (perimeterWidth + perimeterLength);
                          const isSelected = (isFence && selectedFence?.id === element.id) ||
                                             (isBorder && selectedBorder?.id === element.id) ||
                                             (element.model_type === 'floor' && selectedFloor?.id === element.id) ||
                                             (element.model_type === 'foundation' && selectedFoundation?.id === element.id);
                          const colorVariants = getColorVariants(element, cat.name);
                          const isPending = pendingMonument && pendingMonument.id === element.id;
                          const isPendingSimple = pendingElement && pendingElement.id === element.id;
                          const pendingColor = isPending ? pendingMonument.color : null;
                          return (
                            <div key={element.id} className="space-y-1.5">
                              <Card className={`transition-colors ${colorVariants ? '' : 'cursor-pointer'} ${isSelected ? 'border-primary bg-primary/5' : ''} ${(isPending || isPendingSimple) ? 'border-primary ring-2 ring-primary/30 bg-primary/5' : 'hover:border-accent/50'}`}
                                onClick={!colorVariants ? () => handleAddElement(element) : undefined}>
                                <CardContent className="p-2 flex items-center gap-2">
                                  {colorVariants ? (
                                    <div className="flex gap-1 flex-shrink-0">
                                      {colorVariants.map((v, i) => {
                                        const isSelectedV = v.texture_url ? pendingMonument?.texture_url === v.texture_url : pendingColor === v.color;
                                        return (
                                          <button key={i} type="button"
                                            className={`w-7 h-7 rounded-md border-2 overflow-hidden transition-all focus:outline-none focus:ring-2 focus:ring-primary ${isSelectedV ? 'border-primary ring-2 ring-primary scale-110' : 'border-slate-200 hover:border-primary hover:scale-110'}`}
                                            style={v.texture_url ? undefined : { backgroundColor: v.color }}
                                            onClick={(e) => { e.stopPropagation(); const price = getVariantPrice(element, v); setPendingMonument({ ...element, color: v.color, texture_url: v.texture_url, price, colorName: v.name_ru }); }}
                                            aria-label={v.name_ru}>
                                            {v.texture_url ? <img src={v.texture_url} alt="" className="w-full h-full object-cover" /> : null}
                                          </button>
                                        );
                                      })}
                                    </div>
                                  ) : (
                                    <div className="w-7 h-7 rounded-md flex-shrink-0 overflow-hidden bg-slate-200 flex items-center justify-center">
                                      {(element.model_type === 'foundation' && isEarthFoundation(element)) ? (
                                        // Земля: показываем иконку-кусочек текстуры земли
                                        <img src={(process.env.PUBLIC_URL || '') + '/textures/zemlya.jpg'} alt="" className="w-full h-full object-cover" />
                                      ) : element.thumbnail_url ? (
                                        <img src={element.thumbnail_url.startsWith('http') ? element.thumbnail_url : (process.env.REACT_APP_BACKEND_URL || '') + element.thumbnail_url} alt="" className="w-full h-full object-cover" />
                                      ) : (
                                        <div className="w-full h-full" style={{ backgroundColor: element.color }} />
                                      )}
                                    </div>
                                  )}
                                  <div className="flex-1 min-w-0">
                                    <p className="text-xs font-medium text-slate-800 truncate">{element.name_ru}</p>
                                    {!(element.model_type === 'foundation' && isEarthFoundation(element)) && (
                                      <p className="text-[10px] text-slate-500">
                                        {isPerimeterBased
                                          ? `Высота: ${formatDimension(element.height)}`
                                          : element.model_type === 'foundation'
                                            ? formatDimensions(perimeterWidth, perimeterLength, element.height)
                                            : formatDimensions(element.width, element.depth, element.height)}
                                      </p>
                                    )}
                                  </div>
                                  <Badge variant="secondary" className="text-[10px] flex flex-col items-end px-1.5 py-0.5">
                                    {colorVariants ? (
                                      <span>{formatPrice(isPending ? pendingMonument.price : Math.min(...colorVariants.map(v => getVariantPrice(element, v))))}</span>
                                    ) : (
                                      <>
                                        <span>
                                          {element.model_type === 'foundation'
                                            ? formatPrice((element.price || 0) * plotArea)
                                            : isPerimeterBased
                                              ? formatPrice((Number(element.price) || 0) * perimeterMeters)
                                              : element.price_unit === 'sqmeter_wh'
                                                // Гравировка портрета: цена в каталоге уже указана за размер "по умолчанию" (например 25×35),
                                                // поэтому здесь показываем базовую цену, без умножения на ширину/высоту.
                                                ? formatPrice(element.price || 0)
                                                : formatPrice(element.price)}
                                        </span>
                                        {isPerimeterBased && (
                                          <span className="text-[8px] text-slate-400">
                                            {formatPrice(Number(element.price) || 0)}/м
                                          </span>
                                        )}
                                      </>
                                    )}
                                  </Badge>
                                </CardContent>
                              </Card>
                              {isPending ? (
                                <div className="flex gap-1.5 px-1 pt-0.5">
                                  <button type="button" onClick={handleConfirmMonument}
                                    className="flex-1 py-1.5 px-3 rounded-lg text-xs font-medium bg-primary/90 text-white hover:bg-primary transition-colors">
                                    Выбрать {pendingMonument.colorName ? pendingMonument.colorName.toLowerCase() : ''}
                                  </button>
                                  <button type="button" onClick={clearPendingMonument}
                                    className="py-1.5 px-3 rounded-lg text-xs text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-colors">
                                    Отмена
                                  </button>
                                </div>
                              ) : isPendingSimple && (
                                <div className="px-1 pt-1 space-y-1.5">
                                  {pendingElement?.model_type === 'engraving_text' && (
                                    <div className="bg-slate-50 rounded-lg p-2 space-y-1.5 border border-slate-200">
                                      <p className="text-[10px] font-medium text-slate-500 uppercase tracking-wide">Текст гравировки</p>
                                      <input
                                        type="text"
                                        placeholder="Фамилия"
                                        value={fioLastName}
                                        onChange={e => setFioLastName(e.target.value)}
                                        className="w-full text-xs px-2 py-1.5 rounded-md border border-slate-200 focus:outline-none focus:ring-1 focus:ring-primary bg-white"
                                      />
                                      <input
                                        type="text"
                                        placeholder="Имя"
                                        value={fioFirstName}
                                        onChange={e => setFioFirstName(e.target.value)}
                                        className="w-full text-xs px-2 py-1.5 rounded-md border border-slate-200 focus:outline-none focus:ring-1 focus:ring-primary bg-white"
                                      />
                                      <input
                                        type="text"
                                        placeholder="Отчество"
                                        value={fioPatronymic}
                                        onChange={e => setFioPatronymic(e.target.value)}
                                        className="w-full text-xs px-2 py-1.5 rounded-md border border-slate-200 focus:outline-none focus:ring-1 focus:ring-primary bg-white"
                                      />
                                    </div>
                                  )}
                                  <div className="flex gap-1.5">
                                    <button
                                      type="button"
                                      onClick={handleConfirmElement}
                                      className="flex-1 py-1.5 px-3 rounded-lg text-xs font-medium bg-primary/90 text-white hover:bg-primary transition-colors"
                                    >
                                      Добавить
                                    </button>
                                    <button
                                      type="button"
                                      onClick={handleCancelPendingElement}
                                      className="py-1.5 px-3 rounded-lg text-xs text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-colors"
                                    >
                                      Отмена
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })
                      )}
                    </TabsContent>
                  );
                })}
              </Tabs>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Desktop layout
  return (
    <div
      ref={sidebarRef}
      className="pointer-events-auto absolute left-0 top-0 bg-white border-r border-slate-200 shadow-2xl flex flex-col z-50 w-80 max-h-[calc(100dvh-120px)] rounded-br-2xl"
      data-testid="sidebar"
    >
      {/* Header with close button */}
      <div className="p-3 border-b border-slate-200 flex items-center justify-between bg-white/80 flex-shrink-0">
        <div>
          <h1 className="font-display text-base md:text-lg font-semibold text-slate-800">
            iRitual
          </h1>
          <p className="text-xs text-slate-500">
            {isPamyatniki ? 'Конструктор памятников' : 'Конструктор благоустройства'}
          </p>
        </div>
        <Button
          onClick={() => {
            setIsCollapsed(true);
            clearPendingMonument();
          }}
          className="h-9 w-9 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600"
          variant="ghost"
          data-testid="sidebar-collapse"
        >
          <X className="h-5 w-5" />
        </Button>
      </div>

      {/* Categories tabs */}
      <Tabs
        value={activeTab}
        onValueChange={handleTabChange}
        className="flex-1 flex flex-col overflow-hidden min-h-0"
      >
        <TabsList className="flex flex-wrap justify-start gap-1 p-2 h-auto bg-transparent flex-shrink-0">
          {!isPamyatniki && (
            <TabsTrigger
              value="size"
              className="flex flex-col items-center gap-0.5 p-1.5 data-[state=active]:bg-primary data-[state=active]:text-white rounded-lg text-[10px] min-w-[44px]"
              data-testid="tab-size"
            >
              <Maximize2 className="h-3.5 w-3.5" />
              <span>Размер</span>
            </TabsTrigger>
          )}
          {orderedCategories.map((cat) => {
            const Icon = categoryIcons[cat.name] || Package;
            const iconSrc = cat.icon_url
              ? (cat.icon_url.startsWith('http') ? cat.icon_url : (process.env.REACT_APP_BACKEND_URL || '') + cat.icon_url)
              : null;
            return (
              <TabsTrigger
                key={cat.id}
                value={cat.name}
                className="flex flex-col items-center gap-0.5 p-1.5 data-[state=active]:bg-primary data-[state=active]:text-white rounded-lg text-[10px] min-w-[44px]"
                data-testid={`tab-${cat.name}`}
              >
                {iconSrc && !iconLoadFailed.has(cat.id) ? (
                  <img
                    src={iconSrc}
                    alt=""
                    className="h-3.5 w-3.5 object-contain"
                    onError={() => setIconLoadFailed(prev => new Set(prev).add(cat.id))}
                  />
                ) : (
                  <Icon className="h-3.5 w-3.5" />
                )}
                <span className="truncate w-full text-center">{cat.name_ru.split(' ')[0]}</span>
              </TabsTrigger>
            );
          })}
        </TabsList>

        <div className="h-px bg-slate-200 flex-shrink-0 mx-2 my-1" />
        <div className="flex-1 px-2 pb-2 min-h-0 overflow-y-auto">
          <TabsContent value="size" className="mt-0">
            <div className="space-y-3 p-1">
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Ширина</label>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPerimeterSize(Math.max(1, +(perimeterWidth - 0.5).toFixed(2)), perimeterLength)}
                    className="w-8 h-8 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center text-lg font-medium hover:bg-slate-200 active:bg-slate-200"
                    data-testid="width-decrement"
                  >−</button>
                  <div className="relative flex-1">
                    <input
                      type="text"
                      inputMode="decimal"
                      value={widthInput}
                      onChange={(e) => setWidthInput(e.target.value)}
                      onBlur={() => {
                        const v = parseFloat(widthInput.replace(',', '.'));
                        if (!isNaN(v) && v >= 1 && v <= 10) setPerimeterSize(+v.toFixed(2), perimeterLength);
                        else setWidthInput(String(perimeterWidth));
                      }}
                      className="w-full h-8 text-center text-[16px] border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30"
                      data-testid="width-input"
                    />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">м</span>
                  </div>
                  <button
                    onClick={() => setPerimeterSize(Math.min(10, +(perimeterWidth + 0.5).toFixed(2)), perimeterLength)}
                    className="w-8 h-8 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center text-lg font-medium hover:bg-slate-200 active:bg-slate-200"
                    data-testid="width-increment"
                  >+</button>
                </div>
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Длина</label>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPerimeterSize(perimeterWidth, Math.max(1, +(perimeterLength - 0.5).toFixed(2)))}
                    className="w-8 h-8 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center text-lg font-medium hover:bg-slate-200 active:bg-slate-200"
                    data-testid="length-decrement"
                  >−</button>
                  <div className="relative flex-1">
                    <input
                      type="text"
                      inputMode="decimal"
                      value={lengthInput}
                      onChange={(e) => setLengthInput(e.target.value)}
                      onBlur={() => {
                        const v = parseFloat(lengthInput.replace(',', '.'));
                        if (!isNaN(v) && v >= 1 && v <= 10) setPerimeterSize(perimeterWidth, +v.toFixed(2));
                        else setLengthInput(String(perimeterLength));
                      }}
                      className="w-full h-8 text-center text-[16px] border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30"
                      data-testid="length-input"
                    />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">м</span>
                  </div>
                  <button
                    onClick={() => setPerimeterSize(perimeterWidth, Math.min(10, +(perimeterLength + 0.5).toFixed(2)))}
                    className="w-8 h-8 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center text-lg font-medium hover:bg-slate-200 active:bg-slate-200"
                    data-testid="length-increment"
                  >+</button>
                </div>
              </div>
            </div>
          </TabsContent>
          {orderedCategories.map((cat) => {
            // Элементы текущей категории (важно: не путать с глобальным catalog `elements` из стора)
            const catElements = getCategoryElements(cat.id);
            const isFlowerbed = cat.name === 'flowerbed';
            const flowerbedP = isFlowerbed ? catElements.filter((el) => el.flowerbed_shape !== 'rectangle') : [];
            const flowerbedRect = isFlowerbed ? catElements.filter((el) => el.flowerbed_shape === 'rectangle') : [];
            const listToRender = isFlowerbed ? [] : catElements;
            const isFlooring = cat.name === 'flooring';
            const isBorderCategory = cat.name === 'border';
            const isMonument = cat.name === 'monument';
            const isPedestal = cat.name === 'pedestal';

            return (
            <TabsContent key={cat.id} value={cat.name} className="mt-0 space-y-1.5">
              {isFlooring ? (
                (() => {
                  const plotArea = perimeterWidth * perimeterLength;
                  const thicknessM = 0.02;
                  const graniteElement = catElements.find((e) => e.name === 'granite_tiles');

                  // Все активные напольные элементы кроме granite_tiles
                  const regularFloorEls = catElements.filter(e => e.name !== 'granite_tiles' && e.is_active !== false);

                  const activeStones = (stones || []).filter((s) => s.is_active !== false);
                  const orderedStones = [...activeStones].sort((a, b) => {
                    const ao = a.order ?? 0;
                    const bo = b.order ?? 0;
                    if (ao !== bo) return ao - bo;
                    return (a.name_ru || '').localeCompare(b.name_ru || '');
                  });

                  const selectedStoneName = selectedFloor?.colorName;
                  const makeGraniteFloorSelection = (stone) => {
                    const pricePerM3 = Number(stone?.price_per_cubic_meter) || 0;
                    const perM2 = pricePerM3 * thicknessM;
                    setSelectedFloor({
                      ...(graniteElement || {}),
                      name: graniteElement?.name || 'granite_tiles',
                      name_ru: 'Гранитная плитка',
                      price: perM2,
                      price_unit: 'sqmeter',
                      width: 1,
                      depth: 1,
                      height: thicknessM,
                      stone_id: stone?.id,
                      colorName: stone?.name_ru,
                      texture_url: stone?.texture_url || stone?.thumbnail_url || graniteElement?.texture_url,
                      thumbnail_url: stone?.thumbnail_url || graniteElement?.thumbnail_url,
                    });
                  };

                  const floorCard = (element) => {
                    if (!element) return null;
                    const total = Math.round((Number(element.price) || 0) * plotArea);
                    const thumbSrc = element.thumbnail_url
                      ? (element.thumbnail_url.startsWith('http') ? element.thumbnail_url : element.thumbnail_url.startsWith('/api/') ? BACKEND_BASE_URL + element.thumbnail_url : (process.env.PUBLIC_URL || '') + element.thumbnail_url)
                      : null;
                    return (
                      <Card
                        key={element.id}
                        className={`transition-colors cursor-pointer hover:border-accent/50 ${selectedFloor?.id === element.id ? 'border-primary bg-primary/5' : ''}`}
                        onClick={() => setSelectedFloor(element)}
                      >
                        <CardContent className="p-2 flex items-center gap-2">
                          <div className="w-10 h-10 rounded-md flex-shrink-0 overflow-hidden bg-slate-200 flex items-center justify-center">
                            {thumbSrc ? (
                              <img src={thumbSrc} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full" style={{ backgroundColor: element.color }} />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-slate-800 truncate">{element.name_ru}</p>
                            <p className="text-[10px] text-slate-500">{formatDimensions(perimeterWidth, perimeterLength, element.height)}</p>
                          </div>
                          <Badge variant="secondary" className="text-[10px] px-1.5 py-0.5 shrink-0">{formatPrice(total)}</Badge>
                        </CardContent>
                      </Card>
                    );
                  };

                  return (
                    <div className="space-y-2">
                      <div className="space-y-1">
                        <div className="space-y-1.5">
                          {orderedStones.map((stone) => {
                            const pricePerM3 = Number(stone.price_per_cubic_meter) || 0;
                            const volume = plotArea * thicknessM;
                            const total = Math.round(volume * pricePerM3);
                            const isSelected = graniteElement && selectedFloor?.name === graniteElement.name && selectedStoneName === stone.name_ru;
                            const swatchUrl = stone.thumbnail_url || stone.texture_url;
                            const textureSrc = swatchUrl
                              ? (swatchUrl.startsWith('http') ? swatchUrl : swatchUrl.startsWith('/api/') ? BACKEND_BASE_URL + swatchUrl : (process.env.PUBLIC_URL || '') + swatchUrl)
                              : null;
                            return (
                              <Card
                                key={stone.id}
                                className={`transition-colors cursor-pointer hover:border-accent/50 ${isSelected ? 'border-primary bg-primary/5' : ''}`}
                                onClick={() => makeGraniteFloorSelection(stone)}
                              >
                                <CardContent className="p-2 flex items-center gap-2">
                                  <div className="w-10 h-10 rounded-md flex-shrink-0 overflow-hidden bg-slate-200 flex items-center justify-center">
                                    {textureSrc ? (
                                      <img src={textureSrc} alt="" className="w-full h-full object-cover" />
                                    ) : (
                                      <div className="w-full h-full" />
                                    )}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-xs font-medium text-slate-800 truncate">{stone.name_ru}</p>
                                    <p className="text-[10px] text-slate-500">{formatDimensions(perimeterWidth, perimeterLength, thicknessM)}</p>
                                  </div>
                                  <Badge variant="secondary" className="text-[10px] px-1.5 py-0.5 shrink-0">{formatPrice(total)}</Badge>
                                </CardContent>
                              </Card>
                            );
                          })}
                        </div>
                      </div>

                      {/* Все остальные напольные покрытия */}
                      {regularFloorEls.map(floorCard)}
                    </div>
                  );
                })()
              ) : isBorderCategory ? (
                (() => {
                  // Бордюры:
                  // - Бетонный оставляем как есть (за метр)
                  // - Каменный — берём цену из stones (₸/м³) и считаем по объёму облицовки (2 см)
                  const perimeterMeters = 2 * (perimeterWidth + perimeterLength);
                  const thicknessM = 0.02; // 2 см облицовка

                  const activeStones = (stones || []).filter((s) => s.is_active !== false);
                  const orderedStones = [...activeStones].sort((a, b) => {
                    const ao = a.order ?? 0;
                    const bo = b.order ?? 0;
                    if (ao !== bo) return ao - bo;
                    return (a.name_ru || '').localeCompare(b.name_ru || '');
                  });

                  // Для размеров 3D берём параметры из любого бордюра, который есть в админке
                  const anyBorderEl = catElements.find((e) => e.model_type === 'border') || null;
                  const borderHeight = Number(anyBorderEl?.height) || 0.1;
                  const borderWidth = Number(anyBorderEl?.width) || 0.08;
                  const borderSectionWidth = Number(anyBorderEl?.section_width) || 0.5;

                  const swatchSrc = (url) => {
                    if (!url || typeof url !== 'string') return null;
                    const u = url.trim();
                    if (!u) return null;
                    if (u.startsWith('http://') || u.startsWith('https://')) return u;
                    if (u.startsWith('/api/')) return BACKEND_BASE_URL + u;
                    return (process.env.PUBLIC_URL || '') + u;
                  };

                  const isStoneBorderSelected = (stone) =>
                    selectedBorder?.price_unit === 'cubic_meter' && String(selectedBorder?.stone_id || '') === String(stone?.id || '');

                  const pickStoneBorder = (stone) => {
                    const pricePerM3 = Number(stone?.price_per_cubic_meter) || 0;
                    setSelectedBorder({
                      ...(anyBorderEl || {}),
                      id: `stone-border-${stone?.id || 'unknown'}`,
                      name: 'stone_border',
                      name_ru: `Бордюр — ${stone?.name_ru || 'камень'}`,
                      model_type: 'border',
                      price: pricePerM3,
                      price_unit: 'cubic_meter',
                      stone_id: stone?.id,
                      // Геометрия для 3D (как у обычного бордюра)
                      height: borderHeight,
                      // Толщина бордюра по умолчанию 2 см
                      width: thicknessM,
                      section_width: borderSectionWidth,
                      // Параметр для расчёта: толщина облицовки (2см)
                      pricing_thickness_m: thicknessM,
                      // Для вида: пробуем подтянуть текстуру камня
                      texture_url: stone?.texture_url || stone?.thumbnail_url || anyBorderEl?.texture_url,
                      thumbnail_url: stone?.thumbnail_url || anyBorderEl?.thumbnail_url,
                      colorName: stone?.name_ru,
                    });
                  };

                  const calcStoneTotal = (stone) => {
                    const pricePerM3 = Number(stone?.price_per_cubic_meter) || 0;
                    const volume = perimeterMeters * borderHeight * thicknessM;
                    return Math.round(volume * pricePerM3);
                  };

                  return (
                    <div className="space-y-2">
                      {/* Обычные бордюры из админки (бетон и т.п.) */}
                      {listToRender
                        .filter((e) => e.model_type === 'border')
                        .map((element) => {
                          const isSelected = selectedBorder?.id === element.id;
                          return (
                            <Card
                              key={element.id}
                              className={`transition-colors cursor-pointer hover:border-accent/50 ${isSelected ? 'border-primary bg-primary/5' : ''}`}
                              onClick={() => setSelectedBorder(element)}
                            >
                              <CardContent className="p-2 flex items-center gap-2">
                                <div className="w-10 h-10 rounded-md flex-shrink-0 overflow-hidden bg-slate-200 flex items-center justify-center">
                                  {element.thumbnail_url ? (
                                    <img src={element.thumbnail_url.startsWith('http') ? element.thumbnail_url : (process.env.REACT_APP_BACKEND_URL || '') + element.thumbnail_url} alt="" className="w-full h-full object-cover" />
                                  ) : (
                                    <div className="w-full h-full" style={{ backgroundColor: element.color }} />
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-xs font-medium text-slate-800 truncate">{element.name_ru}</p>
                                  <p className="text-[10px] text-slate-500">{`Высота: ${formatDimension(element.height || borderHeight)}`}</p>
                                </div>
                                <Badge variant="secondary" className="text-[10px] flex flex-col items-end px-1.5 py-0.5">
                                  <span>{formatPrice((Number(element.price) || 0) * perimeterMeters)}</span>
                                  {/* У бетонного бордюра не показываем текст "за метр" */}
                                </Badge>
                              </CardContent>
                            </Card>
                          );
                        })}

                      {/* Каменные бордюры (из stones) */}
                      {orderedStones.length > 0 && (
                        <div className="pt-1">
                          <p className="text-[11px] font-medium text-slate-500 px-0.5 mb-1">Камень (облицовка 2 см)</p>
                          <div className="space-y-1.5">
                            {orderedStones.map((stone) => {
                              const total = calcStoneTotal(stone);
                              const isSelected = isStoneBorderSelected(stone);
                              const img = swatchSrc(stone.thumbnail_url || stone.texture_url);
                              return (
                                <Card
                                  key={stone.id}
                                  className={`transition-colors cursor-pointer hover:border-accent/50 ${isSelected ? 'border-primary bg-primary/5' : ''}`}
                                  onClick={() => pickStoneBorder(stone)}
                                >
                                  <CardContent className="p-2 flex items-center gap-2">
                                    <div className="w-10 h-10 rounded-md flex-shrink-0 overflow-hidden bg-slate-200 flex items-center justify-center">
                                      {img ? <img src={img} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full" />}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="text-xs font-medium text-slate-800 truncate">{stone.name_ru}</p>
                                      <p className="text-[10px] text-slate-500">{`Высота ${formatDimension(borderHeight)}, толщина 2 см`}</p>
                                    </div>
                                    <Badge variant="secondary" className="text-[10px] px-1.5 py-0.5 shrink-0">{formatPrice(total)}</Badge>
                                  </CardContent>
                                </Card>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()
              ) : isMonument ? (
                <>
                  <div className="space-y-1.5">
                    <p className="text-[11px] font-medium text-slate-500 px-0.5 text-center">Выберите цвет</p>
                    <div className="grid grid-cols-4 gap-x-2 gap-y-1 justify-items-center">
                      {monumentVariants.map((v, i) => {
                        const selected = selectedMonumentVariantIndex === i;
                        const swatchUrl = v.thumbnail_url || v.texture_url;
                        const textureSrc = swatchUrl
                          ? (swatchUrl.startsWith('http')
                            ? swatchUrl
                            : swatchUrl.startsWith('/api/')
                              ? BACKEND_BASE_URL + swatchUrl
                              : (process.env.PUBLIC_URL || '') + swatchUrl)
                          : null;
                        return (
                          <div key={i} className="flex flex-col items-center gap-1 w-full min-w-0">
                            <button
                              type="button"
                              className={`w-10 h-10 rounded-lg border-2 overflow-hidden transition-all focus:outline-none focus:ring-2 focus:ring-primary shrink-0 ${
                                selected ? 'border-primary ring-2 ring-primary' : 'border-slate-200 hover:border-primary'
                              }`}
                              style={!textureSrc ? { backgroundColor: v.color || '#666' } : undefined}
                              onClick={() => setSelectedMonumentVariantIndex(i)}
                              aria-label={v.name_ru}
                            >
                              {textureSrc ? (
                                <img src={textureSrc} alt="" className="w-full h-full object-cover" />
                              ) : null}
                            </button>
                            <span className="text-[10px] text-slate-600 text-center leading-tight w-full break-words max-w-[4.5rem]">
                              {v.name_ru}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <div className="space-y-1">
                    {catElements.map((element) => {
                      const variant = monumentVariants[selectedMonumentVariantIndex] || monumentVariants[0];
                      const pricePerM3 = getVariantPrice(element, variant);
                      const volume = (element.width || 1) * (element.height || 1) * (element.depth || 1);
                      // Плоская доплата за форму (поле «Цена установки») — чтобы карточка
                      // совпадала с итогом (см. useStore.getTotalPrice).
                      const totalPrice = Math.round(volume * pricePerM3) + (isPamyatniki ? (element.installation_price || 0) : 0);
                      const isPendingThis = pendingMonument && pendingMonument.id === element.id;
                      return (
                        <div key={element.id} className="space-y-1.5">
                          <Card
                            className={`transition-colors cursor-pointer ${isPendingThis ? 'border-primary ring-2 ring-primary/30 bg-primary/5' : 'hover:border-accent/50'}`}
                            onClick={() => setPendingMonument({
                              ...element,
                              element_id: element.id,
                              color: variant.color || '#666',
                              texture_url: variant.texture_url,
                              price: pricePerM3,
                              price_unit: 'cubic_meter',
                              colorName: variant.name_ru
                            })}
                            data-testid={`element-${element.id}`}
                          >
                            <CardContent className="p-2 flex items-center gap-2">
                              <div className="w-10 h-10 rounded-md flex-shrink-0 bg-slate-200 flex items-center justify-center overflow-hidden">
                                {element.thumbnail_url ? (
                                  <img
                                    src={element.thumbnail_url.startsWith('http') ? element.thumbnail_url : (process.env.REACT_APP_BACKEND_URL || '') + element.thumbnail_url}
                                    alt=""
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <Landmark className="w-5 h-5 text-slate-600" />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-xs font-medium text-slate-800 truncate">{element.name_ru}</p>
                                <p className="text-[10px] text-slate-500">{formatDimensions(element.width, element.depth, element.height)}</p>
                              </div>
                              <Badge variant="secondary" className="text-[10px] px-1.5 py-0.5 shrink-0">{formatPrice(totalPrice)}</Badge>
                            </CardContent>
                          </Card>
                          {isPendingThis && (
                            <div className="flex gap-1.5 px-1 pt-0.5">
                              <button type="button" onClick={handleConfirmMonument} className="flex-1 py-1.5 px-3 rounded-lg text-xs font-medium bg-primary/90 text-white hover:bg-primary transition-colors">Добавить</button>
                              <button type="button" onClick={clearPendingMonument} className="py-1.5 px-3 rounded-lg text-xs text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-colors">Отмена</button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              ) : isPedestal ? (
                <>
                  <div className="space-y-1.5">
                    <p className="text-[11px] font-medium text-slate-500 px-0.5 text-center">Выберите цвет</p>
                    <div className="grid grid-cols-4 gap-x-2 gap-y-1 justify-items-center">
                      {monumentVariants.map((v, i) => {
                        const selected = selectedPedestalVariantIndex === i;
                        const swatchUrl = v.thumbnail_url || v.texture_url;
                        const textureSrc = swatchUrl
                          ? (swatchUrl.startsWith('http')
                            ? swatchUrl
                            : swatchUrl.startsWith('/api/')
                              ? BACKEND_BASE_URL + swatchUrl
                              : (process.env.PUBLIC_URL || '') + swatchUrl)
                          : null;
                        return (
                          <div key={i} className="flex flex-col items-center gap-1 w-full min-w-0">
                            <button
                              type="button"
                              className={`w-10 h-10 rounded-lg border-2 overflow-hidden transition-all focus:outline-none focus:ring-2 focus:ring-primary shrink-0 ${
                                selected ? 'border-primary ring-2 ring-primary' : 'border-slate-200 hover:border-primary'
                              }`}
                              style={!textureSrc ? { backgroundColor: v.color || '#666' } : undefined}
                              onClick={() => setSelectedPedestalVariantIndex(i)}
                              aria-label={v.name_ru}
                            >
                              {textureSrc ? (
                                <img src={textureSrc} alt="" className="w-full h-full object-cover" />
                              ) : null}
                            </button>
                            <span className="text-[10px] text-slate-600 text-center leading-tight w-full break-words max-w-[4.5rem]">
                              {v.name_ru}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <div className="space-y-1">
                    {catElements.map((element) => {
                      const variant = monumentVariants[selectedPedestalVariantIndex] || monumentVariants[0];
                      const pricePerM3 = getVariantPrice(element, variant);
                      const volume = (element.width || 1) * (element.height || 1) * (element.depth || 1);
                      const totalPrice = Math.round(volume * pricePerM3);
                      const isPendingThis = pendingElement && pendingElement.id === element.id;
                      return (
                        <div key={element.id} className="space-y-1.5">
                          <Card
                            className={`transition-colors cursor-pointer ${isPendingThis ? 'border-primary ring-2 ring-primary/30 bg-primary/5' : 'hover:border-accent/50'}`}
                            onClick={() => setPendingElement({
                              ...element,
                              element_id: element.id,
                              color: variant.color || '#666',
                              texture_url: variant.texture_url,
                              price: pricePerM3,
                              price_unit: 'cubic_meter',
                              colorName: variant.name_ru
                            })}
                            data-testid={`element-${element.id}`}
                          >
                            <CardContent className="p-2 flex items-center gap-2">
                              <div className="w-10 h-10 rounded-md flex-shrink-0 bg-slate-200 flex items-center justify-center overflow-hidden">
                                {element.thumbnail_url ? (
                                  <img
                                    src={element.thumbnail_url.startsWith('http') ? element.thumbnail_url : (process.env.REACT_APP_BACKEND_URL || '') + element.thumbnail_url}
                                    alt=""
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <RectangleVertical className="w-5 h-5 text-slate-600" />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-xs font-medium text-slate-800 truncate">{element.name_ru}</p>
                                <p className="text-[10px] text-slate-500">{formatDimensions(element.width, element.depth, element.height)}</p>
                              </div>
                              <Badge variant="secondary" className="text-[10px] px-1.5 py-0.5 shrink-0">{formatPrice(totalPrice)}</Badge>
                            </CardContent>
                          </Card>
                          {isPendingThis && (
                            <div className="flex gap-1.5 px-1 pt-0.5">
                              <button type="button" onClick={handleConfirmElement} className="flex-1 py-1.5 px-3 rounded-lg text-xs font-medium bg-primary/90 text-white hover:bg-primary transition-colors">Добавить</button>
                              <button type="button" onClick={handleCancelPendingElement} className="py-1.5 px-3 rounded-lg text-xs text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-colors">Отмена</button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              ) : isFlowerbed ? (
                <>
                  <div className="space-y-1.5">
                    <p className="text-[11px] font-medium text-slate-500 px-0.5 text-center">Выберите материал</p>
                    <div className="grid grid-cols-4 gap-x-2 gap-y-1 justify-items-center">
                      {flowerbedVariants.map((v, i) => {
                        const selected = selectedFlowerbedVariantIndex === i;
                        const swatchUrl = v.thumbnail_url || v.texture_url;
                        const textureSrc = swatchUrl
                          ? (swatchUrl.startsWith('http')
                            ? swatchUrl
                            : swatchUrl.startsWith('/api/')
                              ? BACKEND_BASE_URL + swatchUrl
                              : (process.env.PUBLIC_URL || '') + swatchUrl)
                          : null;
                        return (
                          <div key={i} className="flex flex-col items-center gap-1 w-full min-w-0">
                            <button
                              type="button"
                              className={`w-10 h-10 rounded-lg border-2 overflow-hidden transition-all focus:outline-none focus:ring-2 focus:ring-primary shrink-0 ${
                                selected ? 'border-primary ring-2 ring-primary' : 'border-slate-200 hover:border-primary'
                              }`}
                              style={!textureSrc ? { backgroundColor: v.color || '#666' } : undefined}
                              onClick={() => setSelectedFlowerbedVariantIndex(i)}
                              aria-label={v.name_ru}
                            >
                              {textureSrc ? (
                                <img src={textureSrc} alt="" className="w-full h-full object-cover" />
                              ) : null}
                            </button>
                            <span className="text-[10px] text-slate-600 text-center leading-tight w-full break-words max-w-[4.5rem]">
                              {v.name_ru}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  {/* Выберите тип цветника */}
                  <div className="space-y-1.5">
                    <p className="text-[11px] font-medium text-slate-500 px-0.5 text-center">Выберите тип</p>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => setSelectedFlowerbedType('p')}
                        className={`h-9 rounded-lg border text-xs font-medium transition-colors ${
                          selectedFlowerbedType === 'p'
                            ? 'border-primary bg-primary/5 text-primary'
                            : 'border-slate-200 hover:border-primary text-slate-700'
                        }`}
                      >
                        Полый
                      </button>
                      <button
                        type="button"
                        onClick={() => setSelectedFlowerbedType('rectangle')}
                        className={`h-9 rounded-lg border text-xs font-medium transition-colors ${
                          selectedFlowerbedType === 'rectangle'
                            ? 'border-primary bg-primary/5 text-primary'
                            : 'border-slate-200 hover:border-primary text-slate-700'
                        }`}
                      >
                        Цельный
                      </button>
                    </div>
                  </div>
                  {/* Наполнение цветника */}
                  {selectedFlowerbedType === 'p' && (() => {
                    const flooringCat = categories?.find(c => c.name === 'flooring');
                    const flooringEls = flooringCat ? elements.filter(e => e.category_id === flooringCat.id && e.is_flowerbed_filling === true && e.is_active !== false) : [];
                    const EARTH = { id: '__earth__', name_ru: 'Земля', price: 0, thumbnail_url: '/textures/zemlya.jpg' };
                    const fillingOptions = [EARTH, ...flooringEls];
                    return (
                      <div className="space-y-1.5">
                        <p className="text-[11px] font-medium text-slate-500 px-0.5 text-center">Наполнение</p>
                        <div className="grid grid-cols-3 gap-x-2 gap-y-1 justify-items-center">
                          {fillingOptions.map((opt) => {
                            const isSelected = flowerbedFilling?.id === opt.id || (!flowerbedFilling && opt.id === '__earth__');
                            // Если нет отдельного превью, показываем картинку текстуры
                            const raw = opt.thumbnail_url || opt.texture_url;
                            const src = raw
                              ? (raw.startsWith('http')
                                ? raw
                                : raw.startsWith('/api/')
                                  ? BACKEND_BASE_URL + raw
                                  : (process.env.PUBLIC_URL || '') + raw)
                              : null;
                            return (
                              <div key={opt.id} className="flex flex-col items-center gap-1 w-full min-w-0">
                                <button
                                  type="button"
                                  onClick={() => setFlowerbedFilling(opt.id === '__earth__' ? null : opt)}
                                  className={`w-10 h-10 rounded-lg border-2 overflow-hidden transition-all focus:outline-none shrink-0 ${isSelected ? 'border-primary ring-2 ring-primary' : 'border-slate-200 hover:border-primary'}`}
                                  style={!src ? { backgroundColor: '#8B6914' } : undefined}
                                >
                                  {src ? <img src={src} alt="" className="w-full h-full object-cover" /> : null}
                                </button>
                                <span className="text-[10px] text-slate-600 text-center leading-tight w-full break-words max-w-[4.5rem]">
                                  {opt.name_ru}
                                  {opt.price > 0
                                    ? <><br /><span className="text-slate-400">{opt.price.toLocaleString('ru-RU')} ₸/м²</span></>
                                    : <><br /><span className="text-slate-400">Бесплатно</span></>}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()}
                  {selectedFlowerbedType === 'p' && flowerbedP.length > 0 && (
                    <div className="space-y-1">
                      {flowerbedP.map((element) => {
                        const variant = flowerbedVariants[selectedFlowerbedVariantIndex] || flowerbedVariants[0];
                        const pricePerM3 = getVariantPrice(element, variant);
                        const effectivePriceM = pricePerM3 * ((element.price_ratio ?? 100) / 100);
                        // Объём только камня (3 балки «П»), без внутренней пустоты под землю (как в Scene3D: borderThick 0.08)
                        const w = element.width || 1, h = element.height || 1, d = element.depth || 1;
                        const borderThick = 0.08;
                        const volume = borderThick * h * (2 * d + w);
                        const fillingPrice = flowerbedFilling ? Math.round((Number(flowerbedFilling.price) || 0) * w * d) : 0;
                        const totalPrice = Math.round(volume * effectivePriceM) + fillingPrice;
                        const isPendingThis = pendingElement && pendingElement.id === element.id;
                        return (
                          <div key={element.id} className="space-y-1.5">
                            <Card
                              className={`transition-colors cursor-pointer ${isPendingThis ? 'border-primary ring-2 ring-primary/30 bg-primary/5' : 'hover:border-accent/50'}`}
                              onClick={() => setPendingElement({
                                ...element,
                                element_id: element.id,
                                color: variant.color || '#666',
                                texture_url: variant.texture_url,
                                price: effectivePriceM,
                                price_unit: 'cubic_meter',
                                colorName: variant.name_ru,
                                flowerbed_shape: element.flowerbed_shape ?? 'p'
                              })}
                              data-testid={`element-${element.id}`}
                            >
                              <CardContent className="p-2 flex items-center gap-2">
                                <div className="w-7 h-7 rounded-md flex-shrink-0 overflow-hidden bg-slate-200 flex items-center justify-center">
                                  {element.thumbnail_url ? (
                                    <img src={element.thumbnail_url.startsWith('http') ? element.thumbnail_url : BACKEND_BASE_URL + element.thumbnail_url} alt="" className="w-full h-full object-contain" />
                                  ) : variant.thumbnail_url || variant.texture_url ? (
                                    <img
                                      src={(variant.thumbnail_url || variant.texture_url).startsWith('http')
                                        ? (variant.thumbnail_url || variant.texture_url)
                                        : BACKEND_BASE_URL + (variant.thumbnail_url || variant.texture_url)}
                                      alt=""
                                      className="w-full h-full object-cover"
                                    />
                                  ) : (
                                    <div className="w-full h-full" style={{ backgroundColor: variant.color || '#666' }} />
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-xs font-medium text-slate-800 truncate">{element.name_ru}</p>
                                  <p className="text-[10px] text-slate-500">{formatDimensions(element.width, element.depth, element.height)}</p>
                                </div>
                                <Badge variant="secondary" className="text-[10px] px-1.5 py-0.5">{formatPrice(totalPrice)}</Badge>
                              </CardContent>
                            </Card>
                            <div className="flex gap-1.5 px-1 pt-0.5">
                              <button type="button" onClick={() => handleConfirmFlowerbedElement({ ...element, element_id: element.id, color: variant.color || '#666', texture_url: variant.texture_url, price: pricePerM3 * ((element.price_ratio ?? 100) / 100), price_unit: 'cubic_meter', colorName: variant.name_ru, flowerbed_shape: element.flowerbed_shape ?? 'p' })} className="flex-1 py-1.5 px-3 rounded-lg text-xs font-medium bg-primary/90 text-white hover:bg-primary transition-colors">Добавить</button>
                              <button type="button" onClick={handleCancelPendingElement} className="py-1.5 px-3 rounded-lg text-xs text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-colors">Отмена</button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {selectedFlowerbedType === 'rectangle' && flowerbedRect.length > 0 && (
                    <div className="space-y-1">
                      {flowerbedRect.map((element) => {
                        const variant = flowerbedVariants[selectedFlowerbedVariantIndex] || flowerbedVariants[0];
                        const pricePerM3 = getVariantPrice(element, variant);
                        const effectivePriceR = pricePerM3 * ((element.price_ratio ?? 100) / 100);
                        const volume = (element.width || 1) * (element.height || 1) * (element.depth || 1);
                        const totalPrice = Math.round(volume * effectivePriceR);
                        const isPendingThis = pendingElement && pendingElement.id === element.id;
                        return (
                          <div key={element.id} className="space-y-1.5">
                            <Card
                              className={`transition-colors cursor-pointer ${isPendingThis ? 'border-primary ring-2 ring-primary/30 bg-primary/5' : 'hover:border-accent/50'}`}
                              onClick={() => setPendingElement({
                                ...element,
                                element_id: element.id,
                                color: variant.color || '#666',
                                texture_url: variant.texture_url,
                                price: effectivePriceR,
                                price_unit: 'cubic_meter',
                                colorName: variant.name_ru,
                                flowerbed_shape: 'rectangle'
                              })}
                              data-testid={`element-${element.id}`}
                            >
                              <CardContent className="p-2 flex items-center gap-2">
                                <div className="w-7 h-7 rounded-md flex-shrink-0 overflow-hidden bg-slate-200 flex items-center justify-center">
                                  {element.thumbnail_url ? (
                                    <img src={element.thumbnail_url.startsWith('http') ? element.thumbnail_url : BACKEND_BASE_URL + element.thumbnail_url} alt="" className="w-full h-full object-contain" />
                                  ) : variant.thumbnail_url || variant.texture_url ? (
                                    <img
                                      src={(variant.thumbnail_url || variant.texture_url).startsWith('http')
                                        ? (variant.thumbnail_url || variant.texture_url)
                                        : BACKEND_BASE_URL + (variant.thumbnail_url || variant.texture_url)}
                                      alt=""
                                      className="w-full h-full object-cover"
                                    />
                                  ) : (
                                    <div className="w-full h-full" style={{ backgroundColor: variant.color || '#666' }} />
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-xs font-medium text-slate-800 truncate">{element.name_ru}</p>
                                  <p className="text-[10px] text-slate-500">{formatDimensions(element.width, element.depth, element.height)}</p>
                                </div>
                                <Badge variant="secondary" className="text-[10px] px-1.5 py-0.5">{formatPrice(totalPrice)}</Badge>
                              </CardContent>
                            </Card>
                            <div className="flex gap-1.5 px-1 pt-0.5">
                              <button type="button" onClick={() => handleConfirmFlowerbedElement({ ...element, element_id: element.id, color: variant.color || '#666', texture_url: variant.texture_url, price: effectivePriceR, price_unit: 'cubic_meter', colorName: variant.name_ru, flowerbed_shape: 'rectangle' })} className="flex-1 py-1.5 px-3 rounded-lg text-xs font-medium bg-primary/90 text-white hover:bg-primary transition-colors">Добавить</button>
                              <button type="button" onClick={handleCancelPendingElement} className="py-1.5 px-3 rounded-lg text-xs text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-colors">Отмена</button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              ) : (
              listToRender.map((element) => {
                // Тут тоже: считаем по категории, чтобы оградка/бордюр всегда пересчитывались по периметру
                const isFence = element.model_type === 'fence' || cat.name === 'fence';
                const isBorder = element.model_type === 'border' || cat.name === 'border';
                const isPerimeterBased = isFence || isBorder;
                const perimeterMeters = 2 * (perimeterWidth + perimeterLength);
                const isSelected = (isFence && selectedFence?.id === element.id) || 
                                   (isBorder && selectedBorder?.id === element.id) ||
                                   (element.model_type === 'floor' && selectedFloor?.id === element.id) ||
                                   (element.model_type === 'foundation' && selectedFoundation?.id === element.id);
                const colorVariants = getColorVariants(element, cat.name);
                const isPending = pendingMonument && pendingMonument.id === element.id;
                const isPendingSimple = pendingElement && pendingElement.id === element.id;
                const pendingColor = isPending ? pendingMonument.color : null;

                return (
                  <div key={element.id} className="space-y-1.5">
                    <Card
                      className={`transition-colors ${
                        colorVariants ? '' : 'cursor-pointer'
                      } ${isSelected ? 'border-primary bg-primary/5' : ''} ${
                        (isPending || isPendingSimple) ? 'border-primary ring-2 ring-primary/30 bg-primary/5' : 'hover:border-accent/50'
                      }`}
                      onClick={!colorVariants ? () => handleAddElement(element) : undefined}
                      data-testid={`element-${element.id}`}
                    >
                      <CardContent className="p-2 flex items-center gap-2">
                        {colorVariants ? (
                          <div className="flex gap-1 flex-shrink-0" title="Выберите цвет">
                            {colorVariants.map((v, i) => {
                              const isSelectedV = v.texture_url
                                ? pendingMonument?.texture_url === v.texture_url
                                : pendingColor === v.color;
                              return (
                                <button
                                  key={i}
                                  type="button"
                                  className={`w-7 h-7 rounded-md border-2 overflow-hidden transition-all focus:outline-none focus:ring-2 focus:ring-primary ${
                                    isSelectedV
                                      ? 'border-primary ring-2 ring-primary scale-110'
                                      : 'border-slate-200 hover:border-primary hover:scale-110'
                                  }`}
                                  style={v.texture_url ? undefined : { backgroundColor: v.color }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const price = getVariantPrice(element, v);
                                    setPendingMonument({ ...element, color: v.color, texture_url: v.texture_url, price, colorName: v.name_ru });
                                  }}
                                  aria-label={v.name_ru}
                                >
                                  {v.texture_url ? (
                                    <img
                                      src={v.texture_url}
                                      alt=""
                                      className="w-full h-full object-cover"
                                    />
                                  ) : null}
                                </button>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="w-7 h-7 rounded-md flex-shrink-0 overflow-hidden bg-slate-200 flex items-center justify-center">
                            {(element.model_type === 'foundation' && isEarthFoundation(element)) ? (
                              // Земля: показываем иконку-кусочек текстуры земли
                              <img src={(process.env.PUBLIC_URL || '') + '/textures/zemlya.jpg'} alt="" className="w-full h-full object-cover" />
                            ) : element.thumbnail_url ? (
                              <img src={element.thumbnail_url.startsWith('http') ? element.thumbnail_url : (process.env.REACT_APP_BACKEND_URL || '') + element.thumbnail_url} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full" style={{ backgroundColor: element.color }} />
                            )}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-slate-800 truncate">
                            {element.name_ru}
                          </p>
                          {!(element.model_type === 'foundation' && isEarthFoundation(element)) && (
                            <p className="text-[10px] text-slate-500">
                              {isPerimeterBased
                                ? `Высота: ${formatDimension(element.height)}`
                                : element.model_type === 'foundation'
                                  ? formatDimensions(perimeterWidth, perimeterLength, element.height)
                                  : formatDimensions(element.width, element.depth, element.height)}
                            </p>
                          )}
                        </div>
                        <Badge variant="secondary" className="text-[10px] flex flex-col items-end px-1.5 py-0.5">
                          {colorVariants ? (
                            <span>
                              {formatPrice(
                                isPending
                                  ? pendingMonument.price
                                  : Math.min(...colorVariants.map(v => getVariantPrice(element, v)))
                              )}
                            </span>
                          ) : (
                            <>
                              <span>
                                {element.model_type === 'foundation'
                                  ? formatPrice((element.price || 0) * plotArea)
                                  : isPerimeterBased
                                    ? formatPrice((Number(element.price) || 0) * perimeterMeters)
                                    : element.price_unit === 'sqmeter_wh'
                                      // Гравировка портрета: цена в каталоге уже указана за размер "по умолчанию" (например 25×35),
                                      // поэтому здесь показываем базовую цену, без умножения на ширину/высоту.
                                      ? formatPrice(element.price || 0)
                                      : formatPrice(element.price)}
                              </span>
                              {isPerimeterBased && (
                                <span className="text-[8px] text-slate-400">
                                  {formatPrice(Number(element.price) || 0)}/м
                                </span>
                              )}
                            </>
                          )}
                        </Badge>
                      </CardContent>
                    </Card>
                    {isPending ? (
                      <div className="flex gap-1.5 px-1 pt-0.5">
                        <button
                          type="button"
                          onClick={handleConfirmMonument}
                          data-testid="confirm-monument-btn"
                          className="flex-1 py-1.5 px-3 rounded-lg text-xs font-medium bg-primary/90 text-white hover:bg-primary transition-colors"
                        >
                          Выбрать {pendingMonument.colorName ? pendingMonument.colorName.toLowerCase() : ''}
                        </button>
                        <button
                          type="button"
                          onClick={clearPendingMonument}
                          data-testid="cancel-monument-btn"
                          className="py-1.5 px-3 rounded-lg text-xs text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-colors"
                        >
                          Отмена
                        </button>
                      </div>
                    ) : isPendingSimple && (
                      <div className="px-1 pt-1 space-y-1.5">
                        {pendingElement?.model_type === 'engraving_text' && (
                          <div className="bg-slate-50 rounded-lg p-2 space-y-1.5 border border-slate-200">
                            <p className="text-[10px] font-medium text-slate-500 uppercase tracking-wide">Текст гравировки</p>
                            <input
                              type="text"
                              placeholder="Фамилия"
                              value={fioLastName}
                              onChange={e => setFioLastName(e.target.value)}
                              className="w-full text-xs px-2 py-1.5 rounded-md border border-slate-200 focus:outline-none focus:ring-1 focus:ring-primary bg-white"
                            />
                            <input
                              type="text"
                              placeholder="Имя"
                              value={fioFirstName}
                              onChange={e => setFioFirstName(e.target.value)}
                              className="w-full text-xs px-2 py-1.5 rounded-md border border-slate-200 focus:outline-none focus:ring-1 focus:ring-primary bg-white"
                            />
                            <input
                              type="text"
                              placeholder="Отчество"
                              value={fioPatronymic}
                              onChange={e => setFioPatronymic(e.target.value)}
                              className="w-full text-xs px-2 py-1.5 rounded-md border border-slate-200 focus:outline-none focus:ring-1 focus:ring-primary bg-white"
                            />
                          </div>
                        )}
                        <div className="flex gap-1.5">
                          <button
                            type="button"
                            onClick={handleConfirmElement}
                            className="flex-1 py-1.5 px-3 rounded-lg text-xs font-medium bg-primary/90 text-white hover:bg-primary transition-colors"
                          >
                            Добавить
                          </button>
                          <button
                            type="button"
                            onClick={handleCancelPendingElement}
                            className="py-1.5 px-3 rounded-lg text-xs text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-colors"
                          >
                            Отмена
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
            </TabsContent>
          );
          })}
        </div>
      </Tabs>
    </div>
  );
}
